import { useDisclosure, useToast } from "@chakra-ui/react";
import isEqual from "lodash/isEqual";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState, useContext, useMemo } from "react";
import { postData } from "../utilities/axios";
import { METERS_TO_MILES, calculateLocationRadius } from "../utilities/helpers";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { GoogleMapContext, UtilityDataContext } from "../utilities/contexts";
import { missingLocation } from "../utilities/notifications";
import Router, { useRouter } from "next/router";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import SearchForm from "./SearchForm";
import { PractitionerCard } from "./PractitionerCard";
import BookingRequestModal from "../booking/initiate-booking/BookingRequestModal";
import LogInModal from "../authentication/LogInModal";
import { Button, Dialog, DialogPanel, Transition } from "@headlessui/react";
import { isbot } from "isbot";
import withUtilities from "../hooks/withUtilities";
import Head from "next/head";
import {
  buildCounsellingMeta,
  isGenericCounsellingMeta,
  segmentAtCounselling,
} from "./counsellingMeta";

const locationMapper = {
  Brighton: "brighton",
  brighton: "brighton",
  "brighton-place": "brighton",
  "brighton-and-hove": "brighton",
  hampstead: "hampstead",
  Hampstead: "hampstead",
  "hampstead-green": "hampstead",
  "hampstead-grove": "hampstead",
  "hampstead-square": "hampstead",
  "hampstead-gate": "hampstead",
  Hackney: "hackney",
  hackney: "hackney",
  hackney: "hackney",
  "hackney-downs": "hackney",
  "hackney-central": "hackney",
  "hackney-wick": "hackney",
  "hackney-grove": "hackney",
  "hackney-walk": "hackney",
  "south-east-london": "southEastLondon",
  "South-East-London": "southEastLondon",
  ilford: "ilford",
  Ilford: "ilford",
  liverpool: "liverpool",
  Liverpool: "liverpool",
  belfast: "belfast",
  Belfast: "belfast",
  plymouth: "plymouth",
  Plymouth: "plymouth",
  "kingston-upon-thames": "kingsonUponThames",
  "Kingston-Upon-Thames": "kingstonUponThames",
  manchester: "manchester",
  Manchester: "manchester",
  cardiff: "cardiff",
  Cardiff: "cardiff",
  watford: "watford",
  Watford: "watford",
  sheffield: "sheffield",
  Sheffield: "sheffield",
  leeds: "leeds",
  Leeds: "leeds",
  glasgow: "glasgow",
  Glasgow: "glasgow",
  edinburgh: "edinburgh",
  Edinburgh: "edinburgh",
  preston: "preston",
  Preston: "preston",
  birmingham: "birmingham",
  Birmingham: "birmingham",
  oxford: "oxford",
  Oxford: "oxford",
  "tunbridge-wells": "tunbridgeWells",
  "Tunbridge-Wells": "tunbridgeWells",
  "milton-keynes": "miltonKeynes",
  "Milton-Keynes": "miltonKeynes",
  portsmouth: "portsmouth",
  Portsmouth: "portsmouth",
  whitstable: "whitstable",
  Whitstable: "whitstable",
};

const specialLocationMap = {
  brighton: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Brighton and Hove. You can read about their expertise and approaches, or use our MatchPlus service to find who is best suited to you and your concerns. You can make therapy appointments and pay for them online if that's what you prefer.",
    },
    {
      title: "Mind Brighton and Hove",
      body: "Mind Brighton and Hove is an independent charity that offers various services and support events for mental health - they can be found at 51 New England Street, BN1 4GQ.",
    },
    {
      title: "Sussex Partnership NHS Foundation Trust",
      body: "Brighton and Hove's Clinical Commissioning Group (CCG) has a Mental Health Rapid Response phone line,  available Mon-Fri 8am-10pm and weekends and bank holidays 10am-10pm, on 03003 040078.",
    },
    {
      title: "BHT (Brighton Housing Trust)",
      body: "BHT (Brighton Housing Trust) offers mental health services, in partnership with Threshold Women's Services and Right Here Brighton and Hove. They can be found at BN1 4PH.",
    },
    {
      title: "Brighton and Hove Samaritans",
      body: "Brighton and Hove Samaritans have a 24/7 support line on 01273 772277 (local call charges apply), and a free national line on 116 123. Samaritans Brighton and Hove can be found a short walk from Hove train station at Dubarry House, Newtown Road, Hove, BN3 6AE.",
    },
    {
      title: "The Brighton and Hove Wellbeing Service",
      body: "The Brighton and Hove Wellbeing Service NHS can be contacted on 0300 002 0060, Mon-Fri, 8am - 6pm. You can be referred to their services by your GP, or fill out a self-referral form on their website.",
    },
  ],
  hampstead: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professional therapists and counsellors who can provide therapy and help with mental health in Hampstead. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "Abbey Community Centre",
      body: "Abbey Community Centre CHAPS offers group fitness and sport activities for men over 50 including cooking, gardening, photography as well as offering a Cocaine Anonymous support group. Call 020 7624 8378.",
    },
    {
      title: "Camden Hub",
      body: "Camden Hub Camden mental health wellbeing centre, where 6–8 week prevention services and personalised recovery focused activities are run. There is also a floating support service which provides one-on-one support to help people live independently. Emotional and practical support is available for adults living with mental health problems. The nature of the support varies from person to person.",
    },
    {
      title: "Camden Minds",
      body: "Camden Minds is a dementia involvement group that meets monthly in Belsize Park, supported by the Dementia Engagement and Empowerment Project (DEEP), Camden Carers Centre, and Age UK Camden, welcoming new members who wish to reduce dementia stigma, influence services and policies, and gain peer support through activities like guest speakers, discussions, research participation, and socialising.",
    },
  ],
  hackney: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professional therapists, counsellors and psychologists who can provide therapy and help with mental health in Hackney. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "City and Hackney Wellbeing Network",
      body: "City and Hackney Wellbeing Network is a partnership of 11 mental health agencies providing free, high-quality groups and courses such as mindfulness, CBT, employment, eco-volunteering, money management and debt prevention, to help with issues including stress, anxiety and depression, and help prevent problems from starting or getting worse. For Hackney residents, including people with severe and enduring mental health conditions. 020 8525 2301",
    },
    {
      title: "Specialist CAMHS",
      body: "Specialist CAMHS is an NHS service for children and young people up to 18 years who are dealing with severe, urgent, persistent and/or complex emotional and behavioural and mental health difficulties, and is also part of the process of getting a diagnosis for children and young people with autism or ADHD. You can find them on Homerton Row, E9 6ED.",
    },
    {
      title: "Mind in the City, Hackney, and Waltham Forest",
      body: "Hackney Mind provides local expert services. Their main office is in Haggerston, N1 5QJ and their community hub in Hackney, E9 6ED.",
    },
  ],
  southEastLondon: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in South East London. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "South London and Maudsley NHS Foundation",
      body: "South London and Maudsley NHS Foundation Trust (SLaM) The main provider of mental health care in Southwark and provider of the broadest range of mental health treatment in the UK. SLaM's telephone information line is available 24 hours a day, seven days a week. A member of staff will be available to help advise you how to get help. 0800 731 2864.",
    },
    {
      title: "Samaritans",
      body: "Samaritans - (08457 90 90 90) operates a 24-hour service available every day of the year. If you prefer to write down how you are feeling, or if you are worried about being overheard on the phone, you can email Samaritans at jo@samaritans.org.",
    },
    {
      title: "Papyrus",
      body: "PAPYRUS is a voluntary organisation that supports teenagers and young adults who are feeling suicidal.",
    },
    {
      title: "London and Southwark Mind",
      body: "Lambeth and Southwark Mind The service provides a confidential telephone and email information service that aims to help people make informed choices about a range of mental health-related services. 020 7501 9203 and press option 1 between 10am and 5pm, Monday to Thursday or email at informationservice@lambethandsouthwarkmind.org.uk.",
    },
  ],
  ilford: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Ilford. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "Redbridge Community Mental Health Team",
      body: "Redbridge Community Mental Health Team This service focuses on wellbeing and recovery and offer short term support with the aim of getting patients back to where they want to be in life.",
    },
    {
      title: "Redbridge Access, Assessment and Brief Intervention Team",
      body: "Redbridge Access, Assessment and Brief Intervention Team The access, assessment and brief intervention service is for adults aged 18 to 65 needing community mental health services in the London borough of Redbridge. Telephone 0300 555 1088 or email raa.team@nhs.net.",
    },
  ],
  liverpool: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Ilford. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "Open Door Centre Wirral",
      body: "Aims to make the treatment of mild depression and anxiety accessible and approachable to young people. Based in Liscard, Wallasey, the service is free of charge and includes a drop-in centre, meditation, exercise programmes and counselling.",
    },
    {
      title: "Talk Liverpool",
      body: "Free NHS service offering access to talking therapies, practical support, and employment advice. Aims to help people who have common mental health problems such as feeling stressed, feeling low in mood (depressed) or very nervous (anxiety). Service is available through GPs in Liverpool, a range of voluntary sector organisations and by self-referral.",
    },
    {
      title: "Chasing the Stigma",
      body: "Comedian Jake Mills, from Croxteth Park in Liverpool, was suffering from depression in 2013 and tried to take his own life. He now devotes his time to helping others dealing with depression. Website Chasing the Stigma includes a database of grassroots organisations working to tackle mental health issues.",
    },
    {
      title: "Mersey Care",
      body: "Provides specialist inpatient and community mental health, learning disabilities, addiction management and brain injury services in Merseyside. Services cover alcohol, eating disorders, ADHD, dementia, learning disabilities, and drug dependency. Referrals often come through GPs.",
    },
    {
      title: "Mary Seacole House",
      body: "A multi-cultural mental health resource service, primarily for people from Black and racial minority communities. The centre was established in 1991 by Granby Community Mental Health Group. Staff will offer support, advice and guidance in both practical and emotional matters.",
    },
    {
      title: "Young Person's Advisory Service",
      body: "Works with young people ages 10 to 25. Services include counselling for difficulties affecting emotional, physical and mental wellbeing. There are also group sessions offered around issues such as self harm and anger awareness.",
    },
    {
      title: "Beacon Counselling Trust",
      body: "Counselling charity which employs counsellors who have extensive experience working with a wide range of personal and work related problems.",
    },
  ],
  belfast: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform which can provide therapy and help with mental health in Belfast. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "CAMHS",
      body: "Child and Adolescent Mental Health Service aims to promote emotional well-being and deliver care, treatment and preventative mental health services to children and young people aged 0–18 years of age who experience significant mental health difficulties. The service works in many different ways with children and young people, depending on their needs. This service has multiple locations.",
    },
    {
      title: "GP Out of Hours",
      body: "GP out of hours provides care for people in Belfast when their normal GP surgery is closed. It is not intended to replace normal GP services, but deals with urgent problems that cannot wait until your own GP is available. You should therefore only use this service if you are suddenly ill and you cannot wait until your GP's surgery is open. The service operates weekdays after 6pm, at weekends and during bank holidays. Call 9079 6220 in South and East Belfast or 9074 4447 in North and West Belfast.",
    },
    {
      title: "Combat Stress",
      body: "Combat Stress is the UK's leading Veterans' mental health charity. They provide timely, effective clinical treatment and welfare support to Veterans who suffer from psychological wounds.",
    },
  ],
  plymouth: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Plymouth. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "Livewire Youth Music",
      body: "Livewire Youth Music offers a full range of services to support from youth work support all the way through to counselling from a qualified team member. No need to become a member and no long waiting lists. Young people can self refer or be referred by a parent/carer, teacher, doctor etc. 01752 843570",
    },
    {
      title: "Plymouth and District Mind",
      body: "Plymouth and District Mind has support for the people of Plymouth and surrounding districts, who are experiencing a mental health problem. 01752 512280",
    },
    {
      title: "SHINE",
      body: "SHINE is created by Plymouth University students, counsellors and researchers. Watch, read, listen, find apps and useful links to the best online resources and information about mental health and well-being.",
    },
    {
      title: "Plymouth Options",
      body: "Plymouth Options offers a range of services to people aged 16 and over who are experiencing common mental health problems. 01752 435419",
    },
  ],
  kingstonUponThames: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Kingston. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "Kingston iCope",
      body: "Kingston iCope offers free, confidential help for problems such as stress, worry, anxiety and depression and insomnia. Kingston iCope also offers a range of courses and individual treatments to help people manage symptoms of depression, anxiety and other cognitive, emotional and behavioural difficulties. It is for adults of all ages, from young to old.",
    },
    {
      title: "Mind in Kingston",
      body: "Mind in Kingston aims to promote the understanding of mental health issues and to provide a range of services in the Royal Borough of Kingston to people with support needs due to their mental health. 0208 255 3939.",
    },
    {
      title: "KCHTT",
      body: "The Kingston Crisis Home Treatment Team (KCHTT) provides treatment to patients in an acute crisis in their own home as an alternative to hospital admission. The team also works closely with Lilacs ward to allow early discharge from hospital. 0203 513 2500/2509",
    },
  ],
  manchester: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that has therapists in Manchester and surrounding areas. You can search for them by filling in a questionnaire that aims to find out your main issues and then find a therapist best suited to you.",
    },
    {
      title: "Manchester Mental Health and Social Care Trust",
      body: "Provide a range of NHS services to the residents of Manchester.",
    },
    {
      title: "The Gateway Service",
      body: "First point of contact to access the Trust's mental health services. 0161 882 2400",
    },
    {
      title: "The Sanctuary",
      body: "24 hour crisis support line for adults. It is for people who feel that they are at crisis point. They offer support with issues such as anxiety, panic attacks, depression or suicidal thoughts. 0300 003 7029",
    },
    {
      title: "Crisis Point",
      body: "Short-term residential mental health service for people suffering mental distress aged 18 and over and living in Manchester. 0161 225 9500",
    },
    {
      title: "42nd Street",
      body: "For young people (aged 11 to 25) living in Manchester, Salford and Trafford. They provide a range of support services to young people under stress. 0161 228 7321",
    },
  ],
  cardiff: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Cardiff. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "Dewis Cymru",
      body: "Dewis Cymru provides information about wellbeing in Wales, including advice about local organisations and services that can help.",
    },
    {
      title: "Cardiff and Vale Action for Mental Health",
      body: "Cardiff and Vale Action for Mental Health is a charity working to improve mental health and wellbeing in Cardiff and The Vale of Glamorgan. Supporting voluntary sector, service user and carer involvement in the development of mental health services in the area.",
    },
    {
      title: "Cardiff Mind",
      body: "Cardiff Mind can provide a wide range of accurate, up-to-date information on mental health issues including: specialist mental health services, independent voluntary organisations and self-help groups as well as providing emergency contacts.",
    },
  ],
  watford: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Watford. You can read about their expertise and approaches, or use our MatchPlus service to see who is best suited to you and your concerns. You can make therapy appointments and pay for them online if you wish.",
    },
    {
      title: "HPFT",
      body: "Hertfordshire Partnership University NHS Foundation Trust (HPFT) provides all NHS mental health and specialist learning disability services in Hertfordshire.",
    },
    {
      title: "Single Point of Access",
      body: "Single Point of Access provides for routine or urgent referrals by trained staff who provide advice and guidance through a triage process. 0300 777 0707",
    },
    {
      title: "Out of Hours Adult Services",
      body: "Adult Services (Out of Hours Service) contact GP, emergency services, accident and emergency who can provide access to the Crisis Assessment and Treatment Team. 01438 843322",
    },
  ],
  sheffield: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Sheffield.",
    },
    {
      title: "Sheffield Flourish",
      body: "A charity that offers peer support programmes and other support.",
    },
    {
      title: "Sheffield Mind",
      body: "Sheffield Helpline for anyone in Sheffield affected by mental health issues. Staffed 24 hours a day, 7 days a week. Call 0808 801 0440.",
    },
    {
      title: "The Samaritans",
      body: "To talk to someone confidentially. 0114 276 7277 or 116 123.",
    },
    {
      title: "Action",
      body: "A charity providing services to vulnerable people in South Yorkshire and Derbyshire including accommodation and mental health support. 0114 270 6999.",
    },
    {
      title: "Let’s Talk",
      body: "Directory of emotional wellbeing and mental health services for children and young people in Sheffield.",
    },
  ],
  leeds: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform that lists professionals who can provide therapy and help with mental health in Leeds.",
    },
    {
      title: "MindWell Leeds",
      body: "An NHS-supported website providing mental health help for anyone in Leeds.",
    },
    {
      title: "Single Point of Access",
      body: "Run by Leeds and York Partnership NHS Foundation Trust and available 24 hours a day. 0300 300 1485.",
    },
    {
      title: "MindMate",
      body: "If you're a young person, MindMate can help you understand the way you're feeling and find the right advice and support.",
    },
    {
      title: "WCTS",
      body: "The Women's Counselling and Therapy Service provides counselling and psychotherapy run by women for women in Leeds.",
    },
    {
      title: "Barca",
      body: "BARCA-Leeds is a multi-purpose charity providing specialist services for children, adults and families.",
    },
    {
      title: "St Vincent's",
      body: "St Vincent's Counselling Service offers up to 20 sessions of free counselling to individuals.",
    },
  ],
  glasgow: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform listing therapists who can help with mental health in Glasgow.",
    },
    {
      title: "Health and Wellbeing Directory",
      body: "Gateway to information about health improvement and wellbeing services provided by NHS Greater Glasgow and Clyde.",
    },
    {
      title: "NHS Crisis Service",
      body: "Out of hours crisis service providing short-term intensive community based care. 0845 650 1730.",
    },
    {
      title: "Adult Eating Disorder Service",
      body: "Community-based psychological service treating adults with moderate to severe eating disorders. 0141 232 7055.",
    },
    {
      title: "GAMH",
      body: "Independent charity promoting mental health and wellbeing and providing community support across Glasgow.",
    },
  ],
  edinburgh: [
    {
      title: "Welldoing",
      body: "Welldoing is a therapy platform listing therapists who can provide therapy and mental health support in Edinburgh.",
    },
    {
      title: "Mental Health Assessment Service",
      body: "For urgent mental health assessment. 24 hour phone line 0131 537 6000.",
    },
    {
      title: "The Samaritans",
      body: "24 hour helpline providing emotional support. 116 123.",
    },
    {
      title: "Edinburgh Carers Council",
      body: "Local charity providing free confidential advocacy for carers.",
    },
    {
      title: "The Edinburgh Crisis Centre",
      body: "Run by Penumbra and provides crisis support. 0808 801 0414.",
    },
    {
      title: "EdSpace",
      body: "Provides information about local services, guides, factsheets and self-help material for mental health.",
    },
  ],
  preston: [
    {
      title: "Welldoing",
      body: "Therapy platform with mental health professionals in Preston.",
    },
    {
      title: "Wellbeing and Mental Health Helpline",
      body: "Open 365 days a year. Call 0800 915 4640.",
    },
    {
      title: "Mindsmatter Talking Therapies",
      body: "Free psychological therapies across Lancashire for adults aged 16+. Call 01772 773437.",
    },
    {
      title: "Military Veterans Mental Health Services",
      body: "Support for ex-service personnel experiencing mental health issues. 0300 323 0707.",
    },
  ],
  birmingham: [
    {
      title: "Welldoing",
      body: "Therapy platform listing professionals providing therapy in Birmingham.",
    },
    {
      title: "Birmingham Healthy Minds",
      body: "NHS psychological therapies service offering advice and talking therapies. 0121 301 2525.",
    },
    {
      title: "CMHTs",
      body: "Community Mental Health Teams providing specialist support and care planning.",
    },
    {
      title: "Forward Thinking Birmingham",
      body: "Crisis team providing urgent mental health care options. 0300 300 0099.",
    },
  ],
  oxford: [
    {
      title: "Welldoing",
      body: "Platform listing therapists providing therapy in Oxford and Oxfordshire.",
    },
    {
      title: "Oxfordshire Mind",
      body: "Advice and support for anyone experiencing a mental health problem. 01865 247 788.",
    },
    {
      title: "Oxford Health NHS Foundation Trust",
      body: "Community-focused organisation providing physical and mental health services.",
    },
    {
      title: "Elmore Community Services",
      body: "Support services for marginalised people across Oxfordshire. 01865 200 130.",
    },
  ],
  tunbridgeWells: [
    {
      title: "Welldoing",
      body: "Therapy platform listing therapists available in Tunbridge Wells.",
    },
    {
      title: "Live It Well",
      body: "Information and guidance for mental wellbeing in Kent and Medway.",
    },
    {
      title: "Mental Health Matters",
      body: "24 hour helpline providing support and advice. 0800 107 0160.",
    },
  ],
  miltonKeynes: [
    {
      title: "Welldoing",
      body: "Therapy platform listing professionals offering therapy in Milton Keynes.",
    },
    {
      title: "Talk for Change",
      body: "Free NHS service offering talking therapies for mild to moderate anxiety or depression. 01908 228830.",
    },
    {
      title: "Milton Keynes Urgent Care Team",
      body: "Community team assessing people with severe or enduring mental illness. 01908 725099.",
    },
  ],
  portsmouth: [
    {
      title: "Welldoing",
      body: "Therapy platform listing therapists in Portsmouth and surrounding areas.",
    },
    {
      title: "Samaritans",
      body: "24 hour support line available on 116 123.",
    },
    {
      title: "Grassroots",
      body: "Provides the Stay Alive app for suicide prevention support.",
    },
    {
      title: "The Campaign Against Living Miserably (CALM)",
      body: "Charity dedicated to preventing male suicide. 0800 58 58 58.",
    },
    {
      title: "HOPELineUK",
      body: "Confidential support for under 35s experiencing suicidal thoughts. 0800 068 4141.",
    },
    {
      title: "Portsmouth Abuse and Rape Counselling Services",
      body: "Provides counselling services and training for schools and organisations.",
    },
  ],
  whitstable: [
    {
      title: "Welldoing",
      body: "Therapy platform listing professionals providing therapy in Whitstable and nearby Kent areas.",
    },
    {
      title: "Think Action",
      body: "Offers wellbeing support online, face to face, or in groups.",
    },
    {
      title: "UMCPT",
      body: "Provides CBT and counselling for people in East Kent. Self-referral on 01227 469338.",
    },
    {
      title: "Live Well Kent",
      body: "Free integrated service helping Kent residents improve mental and physical wellbeing. 0800 567 7699.",
    },
  ],
};

const specialLocationComboMap = {
  "london-anxiety": {
    header: `Anxiety Therapists in London`,
    body: `
Anxiety affects millions of people across the UK, and London’s fast-paced urban environment – with high living costs, long commutes and competitive work cultures – can intensify feelings of anxiety for many residents. In a 2025 survey of London adults, 40% reported feeling nervous, anxious or on edge, while over half experienced multiple symptoms of poor mental health. Anxiety doesn’t just feel distressing – it can interfere with work, relationships and daily life. Welldoing helps you connect with qualified anxiety therapists in London who can support you with tailored approaches, whether you want in-person sessions or online therapy.  
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Anxiety</b>
Anxiety is a natural emotional response, but when it becomes persistent or starts affecting everyday functioning it may suggest an anxiety disorder. Nationally, generalised anxiety disorder is among the most prevalent issues seen in adults, highlighting the importance of accessible therapy.
Londoners, in particular, can experience stressors linked to urban living: from navigating transport systems and work pressures to juggling financial concerns and housing challenges, all of which can contribute to anxiety. Recognising these pressures doesn’t make your experience smaller; it shows how environment and context matter in mental wellbeing.</li>

<li><b>How Therapy Can Help</b>
Professional therapy offers a confidential space to explore anxiety, understand its triggers and learn coping strategies that make a real difference in daily life. Common evidence-based approaches include:
•        Cognitive Behavioural Therapy (CBT): Focuses on identifying and restructuring unhelpful thought patterns and behaviours that fuel anxiety.
•        Psychodynamic and integrative therapies: Explore emotional patterns and life experiences that may influence anxiety.
•        Mindfulness and stress-management techniques: Help build resilience and emotional regulation.
A therapist can work with you to identify whether your anxiety is linked to social situations, work stress, panic symptoms, health worries or other triggers, and tailor therapy accordingly. You can filter by type of therapy when searching for a therapist on Welldoing.</li>

<li><b>Support Options in London</b>
In addition to private therapy, there are publicly funded and charitable supports available across the city. NHS Talking Therapies offers free, evidence-based treatments for anxiety disorders, although waiting times are often long. Charities like Anxiety UK provide helplines, information and support resources that complement clinical therapy. Voluntary organisations such as Mind in London offer advice, workshops, advocacy and signposting to local services that can help alongside formal therapy.</li>

<li><b>How to Find a Therapist for Anxiety Through Welldoing</b>
Welldoing makes it easier to find the right anxiety therapist in London for you. With our searchable directory, you can:
•        Filter therapists by specialism (e.g., anxiety, panic, social anxiety), approach and session format.
•        Choose between in-person appointments in London or online sessions to fit your routine.
•        View verified profiles and credentials so you can feel confident about your choice.
Therapy is most effective when there’s a good fit, and Welldoing helps you explore options until you find one that feels right.
Seeking support is a positive step towards managing anxiety, whether you’re just beginning to notice symptoms or have been living with anxiety for years. Start your search for an anxiety therapist in London today with Welldoing, and find the support that helps you live more fully and confidently.</li>
</ul>
`,
  },
  "london-cognitive-behaviour-therapy-(cbt)": {
    header: `CBT Therapists in London – Find Evidence-Based Support`,
    body: `
If you’re looking for CBT therapists in London, Welldoing can help you connect with qualified, experienced professionals offering Cognitive Behavioural Therapy across the city – from Central London practices to online and hybrid appointments that fit busy lives.
London’s fast-paced environment and high-pressure jobs can contribute to stress, anxiety and other mental health challenges. Whether you’re seeking support for the first time or want a therapist with specific expertise, it’s important to understand what CBT is, how it works, and how to find the right therapist for you.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>What Is Cognitive Behavioural Therapy (CBT)?</b>
Cognitive Behavioural Therapy (CBT) is one of the most widely researched and effective forms of psychological treatment. It is a structured, evidence-based talking therapy that helps people understand the connection between their thoughts, emotions and behaviours, and develop practical strategies for change. CBT focuses on current problems and teaches skills you can apply long after therapy sessions have finished. It is recommended by the National Institute for Health and Care Excellence (NICE) for a wide range of mental health conditions.
Unlike more open-ended therapies, CBT tends to be time-limited and goal-oriented, often involving homework tasks, behavioural experiments, and tools to challenge unhelpful thoughts.</li>

<li><b>Conditions Commonly Treated with CBT</b>
CBT has a strong evidence base for treating a variety of challenges including:
•        Anxiety disorders – generalised anxiety, panic attacks, phobias, social anxiety
•        Depression and low mood
•        Obsessive Compulsive Disorder (OCD)
•        Post-traumatic stress disorder (PTSD)
•        Stress, burnout and workplace issues
•        Sleep problems like insomnia
•        Health anxiety and chronic conditions
•        Low self-esteem and confidence issues
Sessions typically involve identifying thinking patterns or behaviours that maintain distress and learning how to shift these with practical strategies. CBT often shows results within 6–20 sessions, though this can vary depending on individual needs.</li>

<li><b>Why Choose CBT in London?</b>
London is home to one of the largest concentrations of specialised mental health professionals in the UK. This means you can find CBT therapists with expertise in specific areas – anxiety, OCD, PTSD, performance stress, workplace burnout, and relational issues – reflecting the city’s diverse population and pressures.
Londoners have a variety of flexible options to access CBT: Face-to-face sessions across boroughs including the City, Canary Wharf, Hackney, Islington, South London and more; online CBT for those with busy schedules or mobility issues; or evening and weekend appointments to fit around work and study.</li>

<li><b>Accredited Qualifications and Professional Standards</b>
When choosing a CBT therapist in London, accreditation and qualifications can reflect a practitioner’s experience with CBT techniques.
•        British Association for Behavioural and Cognitive Psychotherapies (BABCP) – the UK’s gold-standard accrediting body for CBT practitioners (often required by insurers).
•        Health and Care Professions Council (HCPC) – registration for psychologists and psychotherapists.
•        British Association for Counselling and Psychotherapy (BACP) and UK Council for Psychotherapy (UKCP) – additional professional oversight, training and ethical standards.
Not all therapists advertising CBT have specialist CBT training; verifying BABCP accreditation or equivalent postgraduate training ensures you’re working with someone who has completed rigorous, recognised CBT education and clinical practice.
All therapists on Welldoing are professionally qualified and verified annually as members of their professional associations.</li>

<li><b>Costs of CBT Therapy in London</b>
CBT in London reflects the city’s high cost of living, but there are options across a broad range. In Central and high-demand areas, private CBT often ranges from £80–£150+ per session, with assessments sometimes priced higher. For example, at a specialist private practice in Canary Wharf, a 50-minute CBT session is about £140. Many independent practitioners and clinics offer sliding-scale fees based on income or session time.
London also hosts lower-cost and community-based services, such as university-run clinics with fees as low as £15-£69 per session. There are also charities, such as Groundwork London, or training centres (for supervised trainees) with reduced fees.
The NHS Talking Therapies / IAPT programme offers free CBT with self-referral options, though waiting times can vary by borough.</li>

<li><b>What to Expect in a CBT Session</b>
Whether online or in person, a typical CBT session involves initial assessment to understand your background, patterns and goal, as well as structured conversation focused on practical tools and real-world challenges. There will then be homework or between-session tasks to reinforce the skills learned, and goal tracking to regularly review your progress.
Most sessions last around 50 minutes, though some therapists may offer longer or shorter formats depending on needs and pricing.</li>

<li><b>How Welldoing Helps You Find the Right CBT Therapist</b>
Welldoing is designed to make the process of finding your ideal CBT therapist easier by enabling you to:
•        Filter by location (borough, postcode or online)
•        Select by specialism (e.g. anxiety, trauma, OCD)
•        Check qualifications such as BABCP, HCPC, UKCP or BACP
•        View therapist bios and videos
•        Compare session formats, fees and availability
We help connect you with professionals who are not just qualified, but best suited to your needs.</li>
</ul>
`,
  },
  "london-depression": {
    header: `Depression Therapists in London`,
    body: `
Depression affects millions of people across the UK and can touch every part of daily life – from energy and motivation to relationships and work. In a large and fast-paced city like London, pressures such as demanding careers, high living costs and long commutes can intensify feelings of exhaustion or emotional strain. While occasional sadness is part of being human, ongoing low mood or loss of interest in everyday activities may signal something more serious. Welldoing’s directory helps you connect with qualified depression therapists in London who offer professional support, whether you’re looking for in-person sessions across the city or flexible online therapy.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Depression and How Therapy Can Help</b>
Depression can present in many ways. Some people experience persistent sadness, tearfulness or hopelessness, while others notice irritability, emotional numbness or a loss of interest in activities they once enjoyed. You might find it harder to concentrate, struggle with sleep, feel unusually tired, or experience changes in appetite and motivation. Even routine tasks can begin to feel overwhelming.
Professional therapy offers a supportive and confidential environment to explore these experiences. A therapist can help you understand potential triggers, recognise patterns in thinking or behaviour that may reinforce low mood, and develop strategies for coping more effectively.
Different therapeutic approaches may be used depending on your needs. Cognitive Behavioural Therapy (CBT) focuses on identifying and changing negative thought patterns, while psychodynamic therapy explores deeper emotional patterns and past experiences that may influence current feelings. Many therapists use integrative approaches, combining different methods to support your individual goals. On Welldoing, you can filter your search by therapy type to find the psychotherapist or counsellor that’s right for you.</li>

<li><b>Accessing Depression Support in London</b>
One advantage of seeking therapy in London is the wide availability of mental health professionals. As one of the UK’s largest healthcare and professional hubs, the city has a high concentration of trained therapists and counsellors in private practice, specialist clinics and wellbeing centres.
Therapy services are available across many neighbourhoods, from central areas such as the West End and the City to residential boroughs throughout Greater London. Because the capital is well connected by Underground, rail and bus networks, many people find it relatively easy to attend in-person sessions before or after work, during lunch breaks, or close to home. For those with busy schedules or who prefer additional flexibility, many London therapists also offer online sessions.
In addition to private therapy, London residents can access support through NHS Talking Therapies services, as well as charities and community organisations that provide mental health resources, workshops and helplines.</li>

<li><b>Finding a Therapist for Depression in London Through Welldoing</b>
Welldoing’s therapist directory is designed to make the process of finding a therapist in London who can help with depression straightforward and transparent. The platform allows you to explore a wide range of experienced practitioners and narrow down your search based on what matters most to you.
With Welldoing, you can:
•        Filter therapists by specialism, including depression and low mood
•        Search by location within London or choose therapists who offer online sessions
•        Explore different therapeutic approaches, from CBT to integrative therapy
•        Read verified profiles detailing qualifications, experience and areas of expertise
Finding the right therapist often involves identifying someone whose approach and personality feel like a good fit. Welldoing’s directory allows you to compare professionals and take the time to choose a therapist who feels right for you.
Seeking support for depression can be an important step toward improving your wellbeing. By connecting with a qualified therapist, many people find new ways to understand their emotions, develop coping strategies and begin to move forward with greater confidence and resilience.</li>
</ul>
`,
  },
  "london-interpersonal-relationships": {
    header: `Relationship Therapists in London`,
    body: `
Relationships can be one of the most rewarding parts of life, but they can also be challenging. Whether you’re navigating conflict with a partner, struggling with communication, recovering from a betrayal, or trying to understand patterns in your relationships, therapy can offer valuable support. In a city like London – where busy schedules, work pressures and long commutes can leave little time for connection – relationship difficulties are a common reason people seek therapy. Welldoing helps you find experienced relationship therapists in London, including both couples therapists and therapists who work with individuals on relationship issues.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>When Relationship Support Can Help</b>
Relationship difficulties can arise in many forms. Couples may find themselves arguing more often, feeling distant from one another, or struggling to resolve recurring disagreements. Others may be dealing with specific challenges such as rebuilding trust after infidelity, navigating major life transitions, or adjusting to parenthood.
Relationship therapy isn’t only for couples in crisis. Many people seek support earlier, when they notice communication starting to break down or when they want to strengthen their connection and understanding of one another.
Couples therapy provides a structured and neutral space where both partners can speak openly and feel heard. A trained therapist helps guide conversations, ensuring that discussions remain constructive and focused on understanding rather than blame.
For individuals seeking help with relationship issues, therapy may involve exploring personal history, attachment patterns, boundaries and self-esteem. This kind of work can help people develop healthier relationship dynamics in the future, whether with a current partner or in future relationships.
Therapy can also be helpful for individuals who want to explore their relationship patterns outside of a couple context. Working with a therapist can provide insight into attachment styles, communication habits and emotional triggers that shape how you relate to others.</li>

<li><b>Relationship Pressures in London</b>
Living in London can place unique pressures on relationships. Long working hours, commuting across the city and demanding professional environments can leave little time for meaningful connection. Financial stress, particularly around housing and living costs, can also add tension to partnerships.
At the same time, London is a diverse and dynamic city where people often come from different cultural backgrounds or move frequently for work and education. Navigating cross-cultural relationships, long-distance partnerships or blended family dynamics can sometimes add complexity to relationship challenges.
Therapy offers a space to slow down and reflect on these pressures, helping couples and individuals better understand how external stresses interact with their personal relationship dynamics.</li>

<li><b>Finding a Relationship Therapist in London with Welldoing</b>
London has one of the largest concentrations of trained therapists in the UK, making it possible to find specialists in relationship and couples therapy across many areas of the city. From central London clinics to independent practices in neighbourhoods across Greater London, many therapists offer flexible appointment times, including evenings or online sessions for busy professionals.
Welldoing’s therapist directory makes it easier to explore these options and find a relationship therapist who fits your needs. Through the platform you can:
•        Search for therapists who specialise in relationship difficulties or couples therapy
•        Find professionals who work with both couples and individuals
•        Filter by therapeutic approach, location in London, or online availability
•        Read verified profiles outlining each therapist’s qualifications, experience and style of working
Some therapists offer joint sessions for couples, while others may suggest a combination of individual and couples work depending on the situation. Welldoing allows you to compare therapists and choose someone whose expertise feels relevant to your circumstances.
Relationship difficulties can feel overwhelming, particularly when communication has broken down or emotions are running high. Seeking support from a qualified Welldoing therapist can help create space for understanding, reflection and positive change.</li>
</ul>
`,
  },
  "hackney-anxiety": {
    header: `Anxiety Therapists in Hackney`,
    body: `
Anxiety is one of the most common mental health challenges in the UK, and it can feel especially intense in busy urban areas. Hackney, known for its vibrant communities, creative industries and fast-changing neighbourhoods, attracts people from many different backgrounds – but the pace and pressures of city living can also contribute to stress and worry. Balancing demanding work, rising housing costs and busy social lives can leave little space to process how you’re feeling. If anxiety is affecting your sleep, concentration or relationships, speaking with a therapist can help. Welldoing connects you with qualified anxiety therapists in Hackney offering both in-person and online sessions.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Anxiety</b>
Feeling anxious from time to time is a normal response to stress or uncertainty. However, when anxiety becomes persistent or overwhelming it may indicate an anxiety disorder or chronic stress pattern.
People experience anxiety in many ways. Some feel constantly on edge or worried about future events, while others experience physical symptoms such as restlessness, muscle tension, racing thoughts or difficulty sleeping. Panic attacks, social anxiety or persistent health worries can also be part of the picture.
In an area like Hackney, everyday pressures can sometimes intensify these experiences. Many residents juggle demanding jobs in sectors such as technology, media or the creative industries, while also navigating the realities of London housing and commuting. At the same time, the borough’s busy streets, nightlife and dense population can make it difficult to find moments of calm.</li>

<li><b>How Therapy Can Support Anxiety</b>
Therapy provides a confidential and supportive space to explore anxious thoughts and feelings without judgement. A qualified therapist can help you understand what may be driving your anxiety and develop practical strategies for managing it.
Several evidence-based approaches are commonly used in anxiety therapy, such as cognitive behavioural therapy (CBT), psychodynamic psychotherapy, integrative therapy, and mindfulness-based techniques.
Through therapy, you may learn tools for managing anxious thoughts, setting healthier boundaries, improving self-confidence or coping with work and relationship stress. Because anxiety affects everyone differently, therapists adapt their approach to each person’s circumstances and goals.</li>

<li><b>Anxiety Support in Hackney</b>
Hackney has a strong network of community and mental health resources alongside private therapy options. Local NHS Talking Therapies services provide free treatment for common mental health conditions, including anxiety, though waiting times can vary depending on demand.
The borough is also home to wellbeing centres, community initiatives and charitable organisations offering workshops, peer support groups and mental health advice. For many people, combining community resources with individual therapy can provide a balanced support system.
Because Hackney is well connected by Overground stations, bus routes and nearby Underground lines, attending in-person therapy sessions can be relatively convenient whether you live locally or commute from another part of East London.</li>

<li><b>Finding an Anxiety Therapist in Hackney with Welldoing</b>
Welldoing’s therapist directory helps you find qualified anxiety therapists practising in Hackney and the surrounding areas. The platform makes it easy to search for therapists who specialise in anxiety-related concerns and choose someone whose approach suits you.
With Welldoing, you can:
•        Filter therapists by specialism, including anxiety, panic attacks and social anxiety
•        Search for practitioners based in Hackney or offering online sessions
•        Explore different therapeutic approaches such as CBT, integrative therapy or mindfulness-based work
•        Read verified profiles detailing qualifications, experience and areas of expertise
Finding the right therapist is an important part of the process, and Welldoing allows you to compare professionals and explore options until you find someone you feel comfortable working with.
Taking the step to seek help for anxiety can feel daunting, but many people find that therapy provides clarity, practical tools and a renewed sense of control. Start your search for an anxiety therapist in Hackney with Welldoing today and connect with support that helps you feel calmer and more confident in daily life.</li>
</ul>
`,
  },
  "islington-anxiety": {
    header: `Anxiety Therapists in Islington`,
    body: `
Anxiety is a common mental health concern across the UK, and it can be particularly challenging in a busy part of London like Islington. With its mix of lively high streets, demanding professional environments and densely populated neighbourhoods, many residents balance fast-paced work, social commitments and rising living costs. While Islington offers a vibrant cultural scene and strong community life, these pressures can sometimes contribute to persistent stress or worry. Welldoing helps you connect with qualified anxiety therapists in Islington who offer both in-person appointments and online therapy.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Recognising Anxiety</b>
Occasional anxiety is a natural response to uncertainty or pressure. However, when anxious feelings become constant or begin to interfere with everyday life, it may indicate a deeper difficulty that could benefit from professional support.
Anxiety can present in different ways. Some people experience constant worry or overthinking, while others notice physical symptoms such as tension, rapid heartbeat, restlessness or difficulty sleeping. You may also find yourself avoiding certain situations, feeling overwhelmed by responsibilities, or experiencing sudden episodes of panic.
Life in Islington can sometimes amplify these experiences. Many residents work in central London or nearby tech, media and creative sectors, where workloads and expectations can be high. Busy transport hubs like Angel, Highbury & Islington and Farringdon mean many people are constantly on the move, leaving little time to pause or process stress. Over time, this pace of life can contribute to chronic tension or burnout if not addressed.</li>

<li><b>How Therapy Helps with Anxiety</b>
Therapy provides a calm, confidential environment where you can explore what’s behind your anxiety and learn practical ways to manage it. Therapists often help you understand patterns in thinking, behaviour and emotional responses that may be reinforcing anxious feelings.
Different therapeutic approaches can be effective depending on your needs. Cognitive Behavioural Therapy (CBT) focuses on identifying unhelpful thinking patterns and building healthier coping strategies, while psychodynamic therapy explores how past experiences and emotional patterns influence present feelings. Many therapists also use integrative approaches, combining techniques from multiple schools of therapy.
Through therapy, people often learn ways to regulate stress, manage intrusive thoughts, improve self-confidence and develop healthier responses to pressure at work or in relationships.</li>

<li><b>Mental Health Support in Islington</b>
Islington has a strong network of mental health services alongside private therapy practices. NHS Talking Therapies services operate locally, offering free evidence-based treatments for anxiety and related conditions, although waiting lists can sometimes be long.
The borough is also home to community wellbeing initiatives, support groups and charities providing advice, workshops and peer support. These services can complement private therapy and help people build a broader network of support.
With excellent transport links via the Northern, Victoria and Piccadilly lines, as well as Overground and major bus routes, attending therapy in Islington is convenient for many people who live or work nearby. Many therapists also offer online sessions, providing flexibility for those with busy schedules.</li>

<li><b>Finding an Anxiety Therapist in Islington with Welldoing</b>
Welldoing’s therapist directory helps you find experienced anxiety therapists working in Islington and surrounding areas. The platform allows you to explore different therapists and identify someone whose approach feels right for you.
Using Welldoing, you can:
•        Filter therapists by specialism, including anxiety, panic and stress-related difficulties
•        Search for practitioners located in Islington or offering online sessions
•        Explore a range of therapeutic approaches, including CBT and integrative therapy
•        Read verified therapist profiles outlining training, experience and areas of expertise
Finding the right therapist is an important step in the therapeutic process. Welldoing makes it easier to compare options and connect with a professional who can support you. Start your search for an anxiety therapist in Islington today with Welldoing and take a positive step toward greater calm, clarity and resilience.</li>
</ul>
`,
  },
  "hackney-cognitive-behaviour-therapy-(cbt)": {
    header: `CBT Therapists in Hackney – Find Mental Health Support`,
    body: `
If you’re looking for CBT therapists in Hackney, Welldoing can help you connect with qualified professionals offering Cognitive Behavioural Therapy across the borough. Hackney’s vibrant neighbourhoods from Shoreditch and Dalston to Stoke Newington and Hackney Wick are home to many therapists working in private practices, community clinics and online. Whether you prefer in-person sessions locally or flexible online therapy, CBT offers practical, evidence-based tools for managing anxiety, stress, low mood and other challenges in a busy urban environment.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>What Is Cognitive Behavioural Therapy (CBT)?</b>
Cognitive Behavioural Therapy (CBT) is a structured, evidence-based talking therapy that focuses on how thoughts, feelings and behaviours influence one another. Rather than exploring the past in depth, CBT concentrates on current problems and practical strategies that help you change unhelpful patterns.
CBT sessions are usually goal-focused and time-limited, often involving exercises such as identifying negative thinking patterns, behavioural experiments, and small tasks to practise between sessions.
CBT therapists can help with a wide range of concerns, including anxiety, depression, OCD, trauma, stress, low self-esteem, and more. Usually CBT is short term, often lasting between 6 and 30 sessions, although the length varies depending on your individual goals.</li>

<li><b>Why Find a CBT Therapist in Hackney?</b>
Hackney is one of London’s most diverse boroughs, with a population of around 280,000 people and a mix of long-established communities and rapidly growing creative and tech industries. Areas like Shoreditch and Hoxton attract start-ups and freelancers, while Stoke Newington and Clapton are known for community-focused living.
With this growth has come increasing demand for mental health support. Local therapists often work with issues linked to urban living, including work stress, housing pressures, social anxiety, and the impact of rapid neighbourhood change.
Hackney residents can access CBT through:
•        Private practices across neighbourhoods such as Dalston, London Fields and Stoke Newington, easily accessible via Hackney’s public transport connections
•        Online CBT sessions, offering flexibility for busy schedules
•        Evening or hybrid appointments for those balancing work, study or childcare</li>

<li><b>Qualifications and Professional Standards</b>
When choosing a CBT therapist in Hackney, checking professional accreditation is important. Key UK organisations include: BABCP (British Association for Behavioural and Cognitive Psychotherapies), the primary accrediting body for specialist CBT therapists; as well as HCPC (Health and Care Professions Council) which regulates practitioner psychologists, and BACP and UKCP, professional organisations for counsellors and psychotherapists.
All therapists listed on Welldoing are professionally qualified and have their credentials verified annually.</li>

<li><b>Costs of CBT Therapy in Hackney</b>
Private CBT fees in Hackney are often slightly lower than in central London but still reflect the capital’s overall cost of living. Typical prices range from £70 to £120 per 50-minute session, though highly specialised clinicians may charge more.
Some therapists offer sliding-scale fees based on income, reduced-fee trainee clinics, or short-term CBT programmes. You can filter your search on Welldoing by session fees to find the right match for you.
Hackney residents can also access free therapy through the NHS Talking Therapies (formerly IAPT) service, which allows self-referral, though waiting times vary.</li>

<li><b>Finding a CBT Therapist in Hackney</b>
Welldoing makes it easier to find a therapist who suits your needs by allowing you to:
•        Filter therapists by Hackney location or online sessions
•        Search by specialism, such as anxiety, trauma or burnout
•        Check professional accreditations like BABCP or HCPC
•        Compare fees, formats and therapist profiles
The goal is not just to find a qualified CBT therapist, but one whose experience and approach feel right for you.</li>
</ul>
`,
  },
  "chelsea-interpersonal-relationships": {
    header: `Relationship Therapists in Chelsea`,
    body: `
If you’re looking for a relationship therapist in Chelsea, Welldoing can help you connect with experienced professionals who specialise in couples therapy and relationship counselling. Whether you and your partner are navigating conflict, rebuilding trust, or simply wanting to improve communication, therapy offers a supportive space to work through challenges together. Chelsea’s fast-paced professional culture, high cost of living and busy social lifestyles can all place pressure on relationships, making dedicated time for reflection and conversation with a trained therapist especially valuable.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>When Relationship Therapy Can Help</b>
Relationship therapy can support couples and individuals through many different situations. Some people seek help during periods of conflict, while others come to therapy to strengthen an already stable relationship or understand patterns that keep repeating.
Couples therapy provides a structured environment where both partners can speak openly while a trained therapist helps guide the conversation. The focus is usually on understanding each other’s perspectives, identifying patterns that lead to conflict, and developing healthier ways of communicating.
For individuals, relationship-focused therapy may explore attachment styles, boundaries, emotional triggers and past relationship experiences. This kind of work can help people develop stronger and more fulfilling connections in future relationships as well.</li>

<li><b>Relationship Pressures in Chelsea</b>
Chelsea is known for its affluent neighbourhoods, high property prices and strong professional community. Many residents work in demanding sectors such as finance, law, media and entrepreneurship. While these careers can be rewarding, long working hours and high expectations can sometimes make it harder for couples to prioritise time together.
The area also attracts international residents and professionals relocating to London, which can add unique relationship dynamics such as cross-cultural partnerships, relocation stress or limited nearby family support. For couples raising children, balancing demanding careers with family life in an expensive part of London can also create tension.
Relationship therapy offers a space to step back from these pressures and explore how external stressors may be affecting communication, intimacy and connection.</li>

<li><b>Therapy Options in Chelsea</b>
Chelsea has a number of private therapy practices, often located around neighbourhoods such as Sloane Square, King’s Road and South Kensington. Many therapists work from quiet consulting rooms within wellness clinics or shared therapy centres, offering face-to-face sessions in a confidential setting.
Typical relationship therapy sessions last around 50–75 minutes, with many practitioners offering weekly or fortnightly couples’ sessions, individual therapy focused on relationship issues, or online or hybrid appointments for flexibility.
Private therapy fees in Chelsea are generally higher than the UK average, often ranging from £100 to £180 per session depending on the therapist’s experience and qualifications.
Local NHS mental health services can sometimes provide support through GP referral, although waiting lists may apply.</li>

<li><b>Finding a Relationship Therapist in Chelsea</b>
Welldoing’s therapist directory makes it easier to find the right relationship therapist in Chelsea by allowing you to:
•        Search specifically for couples and relationship specialists
•        Filter therapists by Chelsea location or online availability
•        Explore different therapeutic approaches such as integrative therapy, psychodynamic therapy or emotionally focused couples therapy
•        View therapist profiles outlining training, qualifications and experience
Whether you are attending therapy as a couple or seeking support individually, working with a qualified therapist can help create space for honest conversation, deeper understanding and lasting relationship change.</li>
</ul>
`,
  },
  "bristol-anxiety": {
    header: `Anxiety Therapists in Bristol`,
    body: `
Anxiety is one of the most common mental health concerns in the UK, and it can feel particularly intense in dynamic, fast-growing cities. Bristol is known for its creative industries, thriving student population and expanding technology sector, but these same factors can also contribute to stress and uncertainty. Balancing demanding work, rising housing costs and busy social lives can leave little time to slow down. If anxiety is affecting your sleep, relationships or ability to concentrate, speaking with a therapist can help. Welldoing connects you with qualified anxiety therapists in Bristol offering both in-person and online sessions.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Anxiety</b>
Occasional anxiety is a normal response to pressure or uncertainty. However, when worry becomes persistent, overwhelming or difficult to control, it may develop into an anxiety disorder or chronic stress pattern.
People experience anxiety in different ways. Some feel constant unease or worry about future events, while others notice physical symptoms such as racing thoughts, restlessness, muscle tension or trouble sleeping. Panic attacks, social anxiety, health anxiety and persistent overthinking are also common experiences.
In a city like Bristol, everyday pressures can amplify these feelings. The city’s growing population has brought new opportunities alongside challenges such as rising rent, competitive job markets and housing shortages. Many residents work in sectors such as technology, digital media, sustainability and the creative arts, where freelance work, tight deadlines and financial uncertainty can increase stress.
Bristol is also home to two large universities, University of Bristol and University of the West of England, meaning a large student population experiences academic pressure, relocation stress and social anxiety during important life transitions.</li>

<li><b>How Therapy Can Help with Anxiety</b>
Therapy provides a confidential space to explore anxious thoughts and feelings with the support of a trained professional. Working with a therapist can help you understand the underlying causes of anxiety and develop tools to manage it more effectively.
Several therapeutic approaches are commonly used for anxiety treatment, including Cognitive Behavioural Therapy (CBT), psychodynamic therapy, integrative therapy, and mindfulness and body-based techniques.
Through therapy, people often learn strategies for calming the nervous system, managing worry cycles, building confidence and setting healthier boundaries in work and relationships.</li>

<li><b>Anxiety Support in Bristol</b>
Bristol has a strong mental health community, with both private practitioners and community-based services available across the city. Many therapists practise in areas such as Clifton, Redland, Bishopston and the city centre.
Residents can also access free support through NHS Talking Therapies services such as Vita Health Group, which offers treatment for anxiety and depression through self-referral, although waiting times can vary.
Charities and community organisations also play an important role in Bristol’s mental health landscape. For example, Second Step provides community mental health services and wellbeing support across the region.
Because Bristol is relatively compact and well connected by bus routes and cycling infrastructure, attending in-person therapy sessions can be convenient whether you live in the city centre or surrounding neighbourhoods.</li>

<li><b>Finding an Anxiety Therapist in Bristol through Welldoing</b>
Welldoing’s therapist directory makes it easier to find qualified anxiety therapists practising in Bristol. The platform allows you to search for professionals who specialise in anxiety-related concerns and compare different approaches.
With Welldoing, you can:
•        Filter therapists by specialism, including anxiety, panic attacks and social anxiety
•        Search for practitioners based in Bristol or offering online sessions
•        Explore different therapeutic approaches such as CBT, integrative therapy or mindfulness-based therapy
•        Read verified profiles detailing qualifications, experience and areas of expertise
Seeking support for anxiety can feel like a big step, but many people find that therapy helps them gain clarity, develop practical coping strategies and feel more confident in daily life. Welldoing can help you begin your search for an anxiety therapist in Bristol and connect with support that fits your needs.</li>
</ul>
`,
  },
  "bristol-cognitive-behaviour-therapy-(cbt)": {
    header: `CBT Therapists in Bristol`,
    body: `
Bristol has a thriving community of therapists, including many who specialise in Cognitive Behavioural Therapy (CBT). If you’re dealing with anxiety, persistent stress, low mood or unhelpful thinking patterns, CBT offers practical strategies to help you regain a sense of control. Across neighbourhoods such as Clifton, Redland, Bishopston and Bedminster, therapists work from private consulting rooms, wellbeing centres and online platforms. Welldoing helps you find qualified CBT therapists in Bristol so you can choose a professional whose expertise and approach fit your needs.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Cognitive Behavioural Therapy</b>
Cognitive Behavioural Therapy (CBT) is a structured psychological approach designed to help people recognise and change patterns of thinking and behaviour that contribute to emotional distress. Rather than focusing primarily on past experiences, CBT concentrates on present challenges and practical solutions.
In sessions, you might work with your therapist to identify recurring thought patterns, test new ways of responding to stressful situations and build skills for managing difficult emotions. Therapists often suggest exercises or reflections to practise between sessions so the techniques become useful in everyday life.
CBT is widely recommended for several mental health conditions and is commonly used to help people experiencing anxiety, depression, panic attacks, OCD, trauma, stress, and more.</li>

<li><b>Why People in Bristol Seek CBT</b>
Bristol is often ranked among the UK’s most desirable cities to live in, known for its creative culture, independent businesses and strong environmental focus. However, the city’s rapid growth has also brought pressures that can affect mental wellbeing.
With a population approaching half a million residents, Bristol has experienced rising housing costs and a competitive rental market in recent years. Many people working in sectors such as digital technology, media production and start-ups face demanding schedules or freelance income uncertainty. These conditions can contribute to chronic stress and anxiety.
The city also has a large student and graduate population, supported by institutions including University of Bristol and University of the West of England. Academic pressure, relocation to a new city and early career challenges often lead students and young professionals to seek mental health support.
CBT is particularly popular in cities like Bristol because it offers clear, practical techniques that can be applied to everyday situations such as workplace stress, social anxiety or performance pressure.</li>

<li><b>Accessing CBT Services in Bristol</b>
Therapists offering CBT can be found throughout the city. Many practitioners work from consulting rooms in areas such as Clifton, Cotham, Redland and the city centre, while others offer hybrid or fully online appointments.
Alongside private therapy, Bristol residents can also access support through NHS Talking Therapy services, although waiting times may apply.
The city also benefits from a strong voluntary and community sector supporting mental health, meaning some residents combine individual therapy with peer support groups, workshops or wellbeing programmes.</li>

<li><b>Finding a CBT Therapist in Bristol through Welldoing</b>
Welldoing’s therapist directory makes it easier to explore CBT therapists working in Bristol and compare different options before making contact.
Through the platform you can:
•        Search specifically for therapists trained in Cognitive Behavioural Therapy
•        Filter results by Bristol location or online sessions
•        Identify professionals who focus on concerns such as anxiety, stress or burnout
•        Read detailed therapist profiles outlining experience, qualifications and therapeutic style
Choosing a therapist is a personal decision, and it may take time to find someone whose approach feels comfortable. With the right support, CBT can help you develop practical skills to manage stress, challenge negative thinking and improve overall mental wellbeing.</li>
</ul>
`,
  },
  "oxford-anxiety": {
    header: `Anxiety Therapists in Oxford`,
    body: `
Anxiety is one of the most common mental health concerns in the UK, and it can be particularly noticeable in high-achieving and fast-paced environments. In a city like Oxford, known for its academic intensity, competitive workplaces and international community, many people experience pressure that can affect their mental wellbeing. Students, academics, healthcare professionals and local residents alike may find themselves dealing with stress, uncertainty or persistent worry. If anxiety is affecting your sleep, concentration or relationships, therapy can help. Welldoing connects you with qualified anxiety therapists in Oxford offering both in-person and online sessions.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Anxiety</b>
Feeling anxious occasionally is a normal response to challenges or uncertainty. However, when anxious thoughts become persistent, intrusive or difficult to control, they may begin to interfere with everyday life. Anxiety can present in many ways, including chronic worry, physical symptoms like muscle tension or insomnia, as well as panic attacks, social anxiety, and low self-esteem.
Oxford’s unique environment can sometimes intensify these feelings. The city is internationally recognised for academic excellence through institutions such as the University of Oxford, which attracts students and researchers from around the world. While this intellectual culture can be inspiring, it can also create high expectations and performance pressure.
At the same time, Oxford has a relatively small city centre with a large daily commuting population, and rising housing costs have made the local property market increasingly competitive. Many residents juggle demanding work in education, healthcare, publishing or science with long commutes or shared housing, leaving limited time to rest and recharge.</li>

<li><b>How Therapy Can Help with Anxiety</b>
Therapy offers a confidential and supportive space where you can talk openly about your worries and experiences. A trained therapist can help you understand the patterns that contribute to anxiety and develop strategies to manage them more effectively.
Different therapeutic approaches may be used depending on your needs. Common methods include Cognitive Behavioural Therapy (CBT), psychodynamic therapy, integrative therapy, or mindfulness-based techniques.
Working with a therapist can help you develop practical tools to calm anxious thoughts, improve confidence, set healthier boundaries and respond more effectively to stress.</li>

<li><b>Anxiety Support in Oxford</b>
Oxford offers a range of mental health resources alongside private therapy. Many therapists practise in areas such as Jericho, Summertown, Headington and central Oxford, often working from quiet consulting rooms or wellbeing centres.
Residents can also access free treatment through NHS Talking Therapies services, including Oxford Health NHS Foundation Trust. These services usually allow self-referral, although waiting times may vary depending on demand.
Oxford also has a strong network of charities and wellbeing organisations that offer peer support, workshops and community programmes. For example, Restore runs recovery and wellbeing initiatives across the city. Combining community support with individual therapy can provide a broad foundation for managing anxiety.</li>

<li><b>Finding an Anxiety Therapist in Oxford with Welldoing</b>
Welldoing’s therapist directory helps you connect with qualified anxiety therapists working in Oxford and the surrounding areas. The platform makes it easier to explore different professionals and identify someone whose experience matches your needs.
With Welldoing, you can:
•        Search for therapists who specialise in anxiety, panic attacks or stress
•        Filter by Oxford location or online therapy options
•        Explore different therapeutic approaches such as CBT, integrative therapy or mindfulness-based therapy
•        Read verified profiles describing each therapist’s qualifications, experience and areas of focus
Reaching out for support when you’re experiencing anxiety can feel like a big step, but therapy often provides insight, practical coping tools and a greater sense of calm. Welldoing can help you begin your search for an anxiety therapist in Oxford and connect with support that helps you feel more balanced and confident in daily life.</li>
</ul>
`,
  },
  "bath-anxiety": {
    header: `Anxiety Therapists in Bath`,
    body: `
Anxiety affects many people across the UK, and even in a smaller city like Bath it can have a significant impact on daily life. Known for its historic architecture, thriving tourism industry and strong university presence, Bath attracts residents from a wide range of backgrounds. While the city offers a high quality of life, pressures related to work, academic expectations and rising living costs can contribute to stress and persistent worry. If anxiety is affecting your wellbeing, speaking with a therapist can help. Welldoing connects you with qualified anxiety therapists in Bath who offer both in-person and online sessions.  

<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Recognising Anxiety</b>
Feeling anxious from time to time is a natural response to uncertainty or challenging situations. However, when anxiety becomes ongoing or overwhelming, it may begin to affect how you think, feel and function in everyday life.
Bath’s environment can sometimes shape how these feelings show up. The city has a relatively small population but attracts a large number of visitors and students throughout the year. Academic institutions such as University of Bath and Bath Spa University bring thousands of students to the city, many of whom experience academic pressure, relocation stress or uncertainty about the future.
At the same time, Bath’s strong tourism and hospitality sectors can create demanding work environments with irregular hours and seasonal pressures. Combined with the area’s high property prices and cost of living, these factors can contribute to ongoing stress for many residents.</li>

<li><b>How Therapy Can Help</b>
Working with a therapist can provide a calm and confidential environment to explore anxious thoughts and feelings. Rather than trying to manage everything alone, therapy allows you to reflect on what might be driving your anxiety and develop strategies for responding differently.
Therapists who work with anxiety often draw on a range of approaches, including Cognitive Behavioural Therapy (CBT), psychodynamic therapy, integrative therapy, and mindfulness techniques.
Over time, therapy can help people understand their triggers, build resilience and develop practical coping skills for managing anxious thoughts and situations.</li>

<li><b>Anxiety Support in Bath</b>
Although Bath is smaller than many UK cities, it has a strong network of mental health services. Private therapists often practise from consulting rooms in neighbourhoods such as Widcombe, Larkhall and the city centre, with many offering online sessions as well.
Residents can also access free support through NHS Talking Therapies services provided locally by Avon and Wiltshire Mental Health Partnership NHS Trust, although waiting times may vary depending on demand.
Community organisations also contribute to Bath’s wellbeing landscape. Groups such as Bath Mind offer wellbeing programmes, support groups and resources for people experiencing mental health challenges.
Because Bath is relatively compact and easy to navigate by foot, bicycle or local bus routes, attending in-person therapy sessions is often straightforward for those living or working in the city.</li>

<li><b>Finding an Anxiety Therapist in Bath through Welldoing</b>
Welldoing’s therapist directory helps you connect with experienced anxiety therapists practising in Bath and the surrounding Somerset area. The platform allows you to explore different professionals and identify someone whose experience and therapeutic style feel like a good fit.
With Welldoing, you can:
•        Search for therapists who specialise in anxiety-related concerns
•        Filter practitioners based in Bath or offering online sessions
•        Explore different therapeutic approaches such as CBT, integrative therapy or mindfulness-based work
•        Read detailed profiles outlining qualifications, experience and areas of expertise
If anxiety is affecting your daily life, seeking support can be a meaningful step toward feeling calmer and more in control. Welldoing can help you begin your search for a qualified anxiety therapist in Bath and connect with the support you need.</li>
</ul>
`,
  },
  "camden-anxiety": {
    header: `Anxiety Therapists in Camden`,
    body: `
Anxiety affects millions of people in the UK, and it can feel particularly intense in busy parts of London. Camden is known for its creative culture, nightlife, universities and constant movement of people. While the area’s energy can be inspiring, it can also bring pressures related to work, study, housing and social life. When worry, panic or stress begins to interfere with everyday life, professional support can make a difference. Welldoing helps you connect with qualified anxiety therapists in Camden who offer both local appointments and online sessions.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Anxiety and Life in Camden</b>
Everyone feels anxious occasionally, especially during stressful or uncertain periods. But if feelings of fear, worry or tension become frequent or difficult to manage, they may begin to affect your ability to concentrate, sleep or feel relaxed.
Anxiety can appear in many forms. Some people find themselves constantly anticipating problems or imagining worst-case scenarios. Others experience more physical symptoms, such as rapid heartbeat, restlessness, nausea or difficulty switching off at night. Panic attacks and social anxiety are also common experiences.
Certain aspects of Camden life can make these feelings more noticeable. The borough includes some of the busiest areas of North London, from Camden Town and King’s Cross to Bloomsbury. Living in a densely populated urban environment can mean crowded transport, demanding workplaces and limited opportunities to slow down.
Camden also hosts several major universities, including University College London and SOAS University of London, bringing large student communities to the area. Academic competition, financial pressures and adjusting to life in London can contribute to anxiety for many young adults.
At the same time, rising housing costs and fast-paced creative or freelance careers in fields such as media, music and publishing can add another layer of uncertainty for residents.</li>

<li><b>What Anxiety Therapy Involves</b>
Working with a therapist provides a structured space to explore anxious thoughts and emotional responses. Sessions focus on understanding the patterns that contribute to anxiety and learning ways to respond differently.
Therapists who specialise in anxiety may use several approaches, including Cognitive Behavioural Therapy (CBT), psychodynamic psychotherapy, integrative counselling, and mindfulness techniques
These approaches help people recognise triggers, challenge unhelpful thinking patterns and develop practical strategies for calming the nervous system. Therapy may also focus on improving confidence, strengthening boundaries and managing everyday stress more effectively.</li>

<li><b>Local Mental Health Support</b>
Camden has a large network of mental health services alongside private therapy practices. Many therapists work from consulting rooms around Bloomsbury, Hampstead, Kentish Town and Camden Town, often offering flexible appointment times including evenings.
Residents can also access NHS Talking Therapies through Camden and Islington NHS Foundation Trust, which provides treatment for anxiety and depression through self-referral. Waiting times can vary, but these services remain an important source of support.
Community organisations also contribute to mental health support locally. For example, Mind in Camden offers advocacy, wellbeing projects and peer support for people living in the borough.</li>

<li><b>Finding an Anxiety Therapist in Camden</b>
Welldoing’s directory helps you identify therapists who specialise in anxiety and practise in Camden or nearby areas.
Through the platform you can:
•        Filter therapists by location or online availability
•        Search by specialisms such as panic attacks or chronic worry
•        Compare therapeutic approaches and session fees
•        Read verified profiles describing each therapist’s training and experience
Seeking help for anxiety can feel like a big step, but many people find that therapy provides clarity, practical coping tools and a greater sense of stability. Welldoing can help you find an anxiety therapist in Camden who supports you in building a calmer and more balanced daily life.</li>
</ul>
`,
  },
  "clapham-anxiety": {
    header: `Anxiety Therapists in Clapham`,
    body: `
Anxiety is one of the most common mental health concerns in the UK, and it often becomes more noticeable in busy city neighbourhoods. Clapham, in South London, is known for its lively social scene, large community of young professionals and fast-paced lifestyle. While these qualities make the area appealing, they can also create pressures related to work, finances and social expectations. If anxiety is beginning to affect your sleep, focus or relationships, professional support can help. Welldoing connects you with qualified anxiety therapists in Clapham who offer both local and online sessions.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Anxiety</b>
Most people experience anxiety occasionally, especially during stressful situations. However, when anxious thoughts become persistent or difficult to manage, they can begin to affect everyday life.
In areas like Clapham, certain lifestyle factors can contribute to these experiences. The neighbourhood is particularly popular with professionals working in sectors such as finance, consulting, media and technology. Many residents commute daily into central London via London Underground connections. Long working hours combined with commuting and an active social culture can leave little time to unwind.
Clapham is also known for its vibrant nightlife and busy restaurant scene around Clapham High Street and Clapham Common. While this creates a strong sense of community, the social environment can sometimes increase feelings of comparison, burnout or pressure to maintain a particular lifestyle.</li>

<li><b>How Therapy Can Help</b>
Anxiety therapy provides a confidential setting where you can discuss worries and emotional challenges without judgement. Working with a trained therapist allows you to explore the factors contributing to anxiety and develop healthier coping strategies.
Therapists may use several approaches depending on your needs, including Cognitive Behavioural Therapy (CBT), psychodynamic psychotherapy, integrative counselling approaches, or mindfulness and relaxation techniques.
These methods can help identify patterns in thinking and behaviour that contribute to anxiety. Over time, therapy can support you in developing practical tools to manage stress, regulate physical symptoms and approach difficult situations with greater confidence.</li>

<li><b>Mental Health Support in Clapham</b>
Clapham has a well-established network of therapists and wellbeing professionals. Many practitioners work from private consulting rooms in areas such as Clapham Old Town, Clapham South and nearby Battersea, often offering both in-person and online sessions.
Residents can also access NHS Talking Therapies services through South London and Maudsley NHS Foundation Trust, which provides treatment for common mental health difficulties including anxiety and depression. These services usually allow self-referral, though waiting lists may apply.
Community organisations also play a role in supporting mental health in South London. Charities that run local wellbeing programmes and support groups can complement individual private therapy.</li>

<li><b>Finding an Anxiety Therapist in Clapham through Welldoing</b>
Welldoing’s therapist directory helps you find qualified anxiety therapists working in Clapham and surrounding South London neighbourhoods. The platform allows you to explore therapists’ experience and approaches before deciding who to contact.
Using Welldoing, you can:
•        Search for therapists who specialise in anxiety and stress
•        Filter practitioners located in Clapham or offering online sessions
•        Compare therapeutic approaches such as CBT, integrative therapy or mindfulness-based work
•        Read verified profiles detailing qualifications, experience and areas of expertise
Seeking help for anxiety can feel like a significant step, but many people discover that therapy helps them gain clarity, develop practical coping strategies and feel more in control of their lives. Welldoing can help you find an anxiety therapist in Clapham who offers the support you need.</li>
</ul>
`,
  },
  "notting-hill-interpersonal-relationships": {
    header: `Relationship Therapists in Notting Hill`,
    body: `
If you’re searching for a relationship therapist in Notting Hill, Welldoing can help you connect with experienced professionals specialising in couples counselling and relationship-focused therapy. Whether you’re facing recurring disagreements, navigating a major life change or hoping to improve communication, therapy provides a neutral and supportive environment for meaningful conversations. In a neighbourhood like Notting Hill – where busy professional lives, family responsibilities and social expectations can intersect – setting aside time to focus on your relationship can be especially valuable.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>When Relationship Therapy May Be Helpful</b>
Relationships evolve over time, and even strong partnerships can experience periods of tension or uncertainty. Therapy can be useful when communication has become strained, when trust has been damaged or when partners feel emotionally disconnected.
Some couples seek support when facing a specific issue, such as disagreements about finances, parenting or life direction. Others attend therapy proactively, wanting to strengthen their relationship before problems become more entrenched.
During couples therapy sessions, a trained therapist helps guide discussions so that both partners can express themselves safely and constructively. The aim is not to assign blame but to understand patterns within the relationship and develop healthier ways of responding to conflict.
Relationship therapy can also benefit individuals. Some people choose to explore their own relationship history, attachment patterns and communication styles in individual sessions. This work can help clarify recurring difficulties in dating or long-term partnerships and support healthier connections in the future.</li>

<li><b>Relationship Pressures in Notting Hill</b>
Notting Hill is one of West London’s most recognisable neighbourhoods, known for its colourful terraces, independent shops and cultural events such as the Notting Hill Carnival. The area attracts professionals working in creative industries, media, finance and technology, as well as families and international residents.
While the neighbourhood offers a vibrant community atmosphere, certain aspects of life here can place pressure on relationships. Property prices and living costs in the Notting Hill area are among the highest in London, which can create financial stress even for well-established couples. Long working hours and commuting into central London can also make it harder to prioritise time together.
For international residents relocating to the UK, relationship dynamics may also be shaped by adjusting to a new culture, distance from family or building new social networks. These experiences can sometimes create misunderstandings or feelings of isolation within a partnership.
Relationship therapy offers a space to reflect on how these external pressures may be affecting communication, intimacy and emotional connection.</li>

<li><b>Therapy Services in Notting Hill</b>
Notting Hill and the surrounding parts of West London have a well-established network of therapists and counselling centres. Many practitioners work from private consulting rooms near Portobello Road Market, Ladbroke Grove and neighbouring Kensington.
Sessions typically last between 50 and 75 minutes. Therapists may offer, joint sessions for couples or individual therapy focusing on relationship issues. You can filter by therapy type, location and average fee on Welldoing.
West London’s extensive public transport network attending in-person sessions can be easily accessible, although many therapists also offer online sessions to fit around London professionals’ busy schedules.</li>

<li><b>Finding a Relationship Therapist in Notting Hill</b>
Welldoing’s therapist directory makes it easier to explore relationship therapists practising in Notting Hill and nearby West London neighbourhoods.
Using the platform, you can:
•        Search specifically for couples and relationship specialists
•        Filter therapists by Notting Hill location or online availability
•        Compare therapeutic approaches, including integrative therapy or emotionally focused couples’ therapy
•        View detailed therapist profiles outlining qualifications, experience and areas of focus
Whether you attend therapy together or individually, working with a trained therapist can help create space for honest conversation, deeper understanding and meaningful relationship change.</li>
</ul>
`,
  },
  "putney-cognitive-behaviour-therapy-(cbt)": {
    header: `CBT Therapists in Putney`,
    body: `
If you’re looking for CBT therapists in Putney, Welldoing can help you connect with qualified professionals offering Cognitive Behavioural Therapy in Southwest London. Putney combines a residential feel with easy access to central London, attracting commuters, families and young professionals. While the area offers green spaces and a strong community atmosphere, everyday pressures such as demanding jobs and busy commutes can still affect mental wellbeing. CBT provides practical tools to manage anxiety, stress and low mood, helping many people regain confidence and balance.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>What CBT Involves</b>
Cognitive Behavioural Therapy (CBT) is a structured form of talking therapy that focuses on how thoughts, feelings and behaviours interact. The central idea behind CBT is that unhelpful thinking patterns can influence how we feel and act, sometimes creating cycles that maintain anxiety, stress or depression.
In CBT sessions, you and your therapist work collaboratively to identify these patterns and explore alternative ways of responding. Rather than focusing heavily on the past, CBT tends to emphasise present challenges and practical change.
Sessions may involve identifying negative thought patterns, learning techniques to manage anxiety or distress, and setting achievable goals to complete between sessions.
Because CBT is skills-based, many people continue using the strategies they learn long after therapy ends. Courses of treatment are often relatively short-term, sometimes lasting between 6 and 30 sessions, although this varies depending on the issue being addressed.</li>

<li><b>Why People in Putney Seek CBT</b>
Putney is known for its riverside setting and proximity to green spaces such as Putney Heath and Richmond Park, yet many residents lead busy professional lives. The area is a popular base for commuters travelling into central London via London Underground and National Rail services from Putney railway station.
Long working hours, commuting routines and the cost of living in London can create ongoing stress for many people. Professionals working in sectors such as finance, consulting, media and technology often face demanding schedules and high expectations, which can contribute to burnout or anxiety.
Because CBT focuses on practical coping strategies, it is frequently used to address issues that arise in fast-paced environments, including work-related stress, social anxiety and persistent overthinking.</li>

<li><b>Accessing CBT Therapy in Putney</b>
CBT therapists can be found throughout Putney and neighbouring areas such as Barnes, Fulham and Wandsworth. Many practitioners work from private consulting rooms or shared therapy clinics, often offering both face-to-face and online appointments.
Alongside private therapy, residents may also be able to access treatment through NHS Talking Therapies services provided locally by South West London and St George’s Mental Health NHS Trust. These services offer support for anxiety and depression and usually allow self-referral, though waiting lists may vary.
Some people also combine therapy with local wellbeing resources, such as mindfulness groups, exercise programmes or community initiatives in the area.</li>

<li><b>Finding a CBT Therapist in Putney through Welldoing</b>
Welldoing’s therapist directory makes it easier to identify CBT therapists working in Putney and surrounding parts of Southwest London.
Through the platform, you can:
•        Search specifically for therapists trained in Cognitive Behavioural Therapy
•        Filter practitioners by Putney location or online availability
•        Explore therapists who specialise in anxiety, depression or stress
•        Read verified profiles detailing qualifications, experience and therapeutic approach
Finding the right therapist can take time, but access to a range of qualified professionals helps you choose someone whose style and expertise match your needs. With the right support, CBT can provide practical tools for managing stress and improving everyday wellbeing.</li>
</ul>
`,
  },
  "brighton-anxiety": {
    header: `Anxiety Therapists in Brighton`,
    body: `
Brighton is known for its seaside setting, creative culture and welcoming community, but like any busy city it can bring pressures that affect mental wellbeing. From work stress to social expectations, many residents experience periods of anxiety that feel difficult to manage alone. If worry, panic or constant overthinking are starting to interfere with daily life, speaking with a therapist can help. Welldoing connects you with qualified anxiety therapists in Brighton who offer both in-person sessions and online support.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Recognising Anxiety</b>
Anxiety is a natural response to uncertainty or perceived threats. In small amounts it can be useful, helping people stay alert or motivated. However, when anxious feelings become frequent or overwhelming, they may begin to affect mood, concentration, sleep and relationships.
Life in Brighton can shape how anxiety manifests. The city has a population of around 280,000 and attracts a wide mix of students, creative professionals, remote workers and commuters. Many residents work in industries such as digital media, hospitality, arts and tourism, which can involve irregular hours, freelance contracts or financial uncertainty.
Brighton also has a strong student presence through institutions such as University of Brighton and University of Sussex. Academic pressure, relocation to a new city and the challenges of early adulthood can all contribute to anxiety among students and young professionals.
Although Brighton is famous for its relaxed coastal lifestyle, busy areas such as Brighton Palace Pier and The Lanes can become crowded and stimulating, which may feel overwhelming for some people, particularly during tourist seasons.</li>

<li><b>How Anxiety Therapy Works</b>
Therapy offers a structured space where you can explore anxious thoughts and emotions with the support of a trained professional. Instead of trying to manage everything alone, therapy allows you to step back and understand the patterns that contribute to anxiety.
Talking therapy approaches to help you recognise triggers, challenge unhelpful thinking patterns and develop practical strategies for coping with stress. Over time, therapy can help reduce the intensity of anxiety and build confidence in dealing with difficult situations.</li>

<li><b>Mental Health Resources in Brighton</b>
Brighton has a strong network of mental health professionals and wellbeing services. Many therapists practise in neighbourhoods such as Hove, Hanover, Seven Dials and central Brighton, often working from private consulting rooms or shared therapy clinics.
Residents can also access free support through NHS Talking Therapies services delivered locally by Sussex Partnership NHS Foundation Trust, although waiting times can vary with demand.
In addition, community organisations play an important role in supporting wellbeing in the city. For example, Mind Brighton and Hove offers workshops, peer support groups and mental health advice for local residents.</li>

<li><b>Finding an Anxiety Therapist in Brighton</b>
Welldoing’s therapist directory helps you discover experienced anxiety therapists practising in Brighton and surrounding parts of Sussex. The platform allows you to explore different therapists and compare their approaches before making contact.
Using Welldoing, you can:
•        Search for therapists who specialise in anxiety and stress
•        Filter results by Brighton location or online sessions
•        Compare therapeutic approaches such as CBT, integrative therapy or mindfulness teachniques
•        Read verified profiles outlining qualifications, experience and areas of expertise
Seeking support for anxiety can feel like a big step, but many people find that therapy helps them gain perspective and develop practical coping skills. Welldoing can help you find an anxiety therapist in Brighton who offers the right support for your needs.</li>
</ul>
`,
  },
  "brighton-cognitive-behaviour-therapy-(cbt)": {
    header: `CBT Therapists in Brighton`,
    body: `
If you’re searching for CBT therapists in Brighton, Welldoing can help you find qualified practitioners offering Cognitive Behavioural Therapy across the city. Brighton’s creative industries, strong student community and busy tourism sector create an energetic atmosphere, but they can also bring pressures linked to work, finances and social life. CBT is a practical, evidence-based therapy that helps people manage anxiety, stress and low mood by developing healthier thinking patterns and coping strategies.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>What is CBT?</b>
Cognitive Behavioural Therapy (CBT) is a structured form of psychological treatment widely used for common mental health difficulties. The approach focuses on the relationship between thoughts, emotions and behaviours, helping people understand how these patterns influence one another.
Rather than concentrating primarily on past experiences, CBT tends to focus on current challenges and the changes that can be made in day-to-day life. A typical CBT programme may involve recognising negative though patterns, learning coping strategies, and practising exercises between sessions to reinforce new skills.
Because CBT emphasises practical tools, many people find that the techniques remain useful long after therapy ends. Some treatment plans are relatively short-term, often lasting several weeks, though the exact length depends on individual goals.</li>

<li><b>Why Brighton Residents Seek CBT</b>
Brighton is widely known for its arts scene, independent businesses and coastal lifestyle. Yet beneath its relaxed reputation, many residents experience the same pressures found in other thriving UK cities.
The local economy includes sectors such as digital media, hospitality, education and tourism. Freelance or seasonal work can sometimes bring financial uncertainty, while busy hospitality schedules may involve long or irregular hours. These factors can contribute to chronic stress or burnout.
Brighton also attracts a large student population from University of Brighton and University of Sussex. Academic expectations, relocation and early career uncertainty can all contribute to anxiety and low mood during this stage of life.
At the same time, popular areas like Brighton Palace Pier and the bustling shopping lanes draw large numbers of visitors throughout the year. While tourism helps define the city’s character, crowded environments and a busy social culture can sometimes feel overwhelming for people already dealing with stress.
CBT is often chosen because it focuses on practical solutions that can be applied directly to everyday challenges such as work stress, social anxiety or persistent overthinking.</li>

<li><b>Accessing CBT Therapy in Brighton</b>
Brighton has a well-established network of therapists and wellbeing services. Many CBT practitioners work from consulting rooms in neighbourhoods such as Hove, Seven Dials, Preston Park and central Brighton, with some offering evening or online appointments.
Residents may also be able to access CBT through NHS Talking Therapies services delivered by Sussex Partnership NHS Foundation Trust, although waiting times may vary depending on demand.
Community organisations and wellbeing groups in the city also offer workshops, peer support and mental health resources that can complement individual therapy.</li>

<li><b>Finding a CBT Therapist in Brighton</b>
Welldoing’s therapist directory helps you explore CBT therapists practising in Brighton and nearby areas. The platform makes it easier to compare professionals and find someone whose expertise fits your needs.
With Welldoing, you can:
•        Search specifically for therapists trained in CBT
•        Filter results by Brighton location or online availability
•        Identify therapists who specialise in concerns such as anxiety, stress or depression
•        Read verified profiles outlining qualifications, experience and therapeutic style
Choosing the right therapist is an important part of the process. With access to experienced CBT practitioners in Brighton, you can find support that helps you develop practical tools for managing stress and improving your overall wellbeing.</li>
</ul>
`,
  },
  "reading-anxiety": {
    header: `Anxiety Therapists in Reading`,
    body: `
Reading is a fast-growing town with a strong business community and excellent transport links, attracting commuters, students and professionals from across the UK and beyond. While the area offers many opportunities, the pace of modern life can also bring pressures that affect mental wellbeing. If ongoing worry, panic or stress is beginning to impact your sleep, relationships or daily routine, speaking with a therapist can help. Welldoing connects you with qualified anxiety therapists in Reading offering both in-person and online sessions.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>Understanding Anxiety</b>
Anxiety is a common human response to uncertainty or perceived risk. In moderate amounts it can help people prepare for challenges or stay focused. However, when anxious thoughts become persistent or overwhelming, they may start to interfere with daily life.
Certain aspects of life in Reading can contribute to these feelings of anxiety. The town has grown rapidly in recent decades and is now a major economic hub in the Thames Valley. Many residents work in technology, finance, pharmaceuticals or telecommunications, often in high-pressure roles linked to nearby business parks and corporate headquarters.
Reading is also a key commuter location due to its direct rail connections to London. Travelling into the capital can mean long days and limited time to rest. Balancing demanding work schedules with commuting and family responsibilities can sometimes lead to chronic stress.
The town also has a strong student presence thanks to the University of Reading, bringing thousands of students to the area each year. Academic expectations, moving away from home and planning future careers can all contribute to anxiety among young adults.</li>

<li><b>How Therapy Can Help with Anxiety</b>
Working with a therapist provides an opportunity to explore anxious thoughts and feelings in a supportive and confidential environment. Therapy allows you to better understand what might be driving anxiety and develop strategies for responding differently.
Therapists who work with anxiety often use a variety of evidence-based approaches. These might include cognitive behavioural techniques, psychodynamic therapy, integrative counselling or mindfulness-based practices.
Through therapy, you may learn to recognise patterns that trigger anxious thinking, develop ways to regulate physical stress responses and build confidence in managing challenging situations. Over time, many people find that therapy helps them feel calmer, more focused and better able to cope with everyday pressures.</li>

<li><b>Mental Health Support in Reading</b>
Reading has a wide range of mental health resources alongside private therapy practices. Many therapists operate from consulting rooms in central Reading, Caversham and nearby Berkshire towns, with many also offering online sessions.
Residents may also be able to access free treatment through NHS Talking Therapies services provided locally by Berkshire Healthcare NHS Foundation Trust, which supports people experiencing anxiety and depression. Self-referral is often possible, though waiting times can vary depending on demand.
Local charities and community organisations also contribute to wellbeing in the area, providing peer support groups, workshops and mental health education.</li>

<li><b>Finding an Anxiety Therapist in Reading</b>
Welldoing’s therapist directory helps you find experienced anxiety therapists practising in Reading and surrounding areas. The platform allows you to explore different professionals and choose someone whose expertise fits your needs.
With Welldoing, you can:
•        Search for therapists who specialise in anxiety-related concerns
•        Filter practitioners based in Reading or offering online appointments
•        Compare therapeutic approaches such as CBT, integrative therapy or mindfulness-based work
•        Read verified profiles detailing qualifications, experience and areas of expertise
Taking the first step to seek support for anxiety can feel daunting, but many people find that therapy provides clarity and practical tools for managing stress. Welldoing can help you find an anxiety therapist in Reading who supports your path toward improved wellbeing.</li>
</ul>
`,
  },
  "guildford-anxiety": {
    header: `Anxiety Therapists in Guildford`,
    body: `
Guildford offers a mix of historic charm, green surroundings and a growing professional community. Many people move to the town for its quality of life and easy links to London, yet everyday pressures can still take a toll on mental wellbeing. Demanding careers, study commitments and financial stress can all contribute to anxiety. If constant worry, panic or tension are affecting your daily life, speaking with a therapist can help. Welldoing connects you with qualified anxiety therapists in Guildford offering in-person and online support.
<ul style="list-style: disc; padding-left: 1.5rem;">
<li><b>What Anxiety Can Feel Like</b>
Anxiety is a natural reaction to stress or uncertainty. It can help people prepare for challenges or stay alert in unfamiliar situations. However, when anxious thoughts or physical symptoms become frequent, they may begin to disrupt work, relationships or sleep.
Some people experience anxiety as persistent worry about the future or difficulty switching off mentally. Others notice physical sensations such as a racing heart, muscle tension, restlessness or fatigue. Panic attacks, social anxiety and excessive self-criticism are also common ways anxiety can appear.
Living in Guildford can sometimes amplify these experiences. The town has become a popular location for professionals who commute into London. Long working hours combined with commuting routines can leave little time to rest or recover from stress.
Guildford also has a strong student population through University of Surrey. Students often face academic expectations, financial pressure and the challenges of adjusting to independent life, all of which can contribute to anxiety.</li>

<li><b>The Role of Therapy</b>
Anxiety therapy provides a confidential space to explore worries and emotional responses with the guidance of a trained professional. Rather than simply trying to suppress anxious thoughts, therapy focuses on understanding the patterns that maintain them.
Therapists may use several approaches when working with anxiety, including cognitive behavioural techniques, integrative counselling, psychodynamic therapy, and mindfulness.
These approaches aim to help you recognise triggers, develop healthier ways of thinking and learn strategies for managing physical stress responses. Over time, therapy can help reduce the intensity of anxiety and improve confidence when facing difficult situations.</li>

<li><b>Mental Health Support in Guildford</b>
Guildford has a range of therapy services alongside NHS mental health support. Many therapists practise from consulting rooms in the town centre, Stoughton and nearby villages, and many offer remote sessions for flexibility.
Residents may also access NHS Talking Therapies through Surrey and Borders Partnership NHS Foundation Trust, which provides treatment for anxiety and depression through self-referral. Waiting times can vary depending on demand.
Community organisations in Surrey also offer wellbeing programmes, peer support groups and workshops that can complement individual therapy.</li>

<li><b>Finding an Anxiety Therapist in Guildford</b>
Welldoing’s therapist directory makes it easier to explore anxiety therapists working in Guildford and surrounding areas.
Through the platform you can:
•        Search for therapists who specialise in anxiety and stress
•        Filter practitioners located in Guildford or offering online sessions
•        Compare therapeutic approaches such as CBT, integrative therapy or mindfulness-based work
•        Read verified profiles outlining qualifications, experience and areas of focus
Seeking help for anxiety can feel like a big step, but many people find that therapy provides clarity and practical tools for coping with stress. Welldoing can help you find an anxiety therapist in Guildford who supports your path toward greater wellbeing.</li>
</ul>
`,
  },
};

const modalityMapper = {
  // cbt: "Cognitive behaviour therapy (CBT)",
  // "cognitive-behaviour-therapy-(cbt)": "Cognitive behaviour therapy (CBT)",
  relationships: "interpersonal-relationships",
  "interpersonal-relationships": "interpersonal-relationships",
  relationship: "interpersonal-relationships",
};

// Give us a random seed that will remain for empty search
const searchSeed = Math.floor(Math.random() * 10000);

const PractitionerSearch = (props) => {
  const isServer = typeof window === "undefined";
  /*
  1. Filters
  dispatch(updatePractitionerFilters) updates the filters - this is done when items are selected or when the selection Tags are closed
  state.practitionerFilters gives the current state

  2. Query
  When practitionerFilters is changed, recalculate the query
  profileListState is driven by state.profileList.list
  state.profileList is updated by searchQuery launching dispatch(updateProfileList(list));
  searchQuery is set by state.searchPractitionerQuery
  dispatch updatePractitionerQuery updates the db query
  */
  const initialHasLocation = useMemo(() => {
    const original = props.search?.original ?? [];
    const first = original[0];
    if (!first) return false;

    const isModality = (
      props.search?.therapistPractitionerModalities ?? []
    ).some(({ label }) => label.toLowerCase() === first.toLowerCase());

    const hasLocationValue =
      props.search?.location?.place || props.search?.location?.outcode;

    return !isModality && Boolean(hasLocationValue);
  }, [props.search]);

  const toSlug = useCallback(
    (v) =>
      typeof v === "string" ? v.toLowerCase().trim().replace(/\s+/g, "-") : "",
    [],
  );
  const toTitleCase = useCallback(
    (v) =>
      typeof v === "string"
        ? v
            .toLowerCase()
            .replace(/-/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase())
        : null,
    [],
  );
  const { googleMapsLoaded, googleMaps } = useContext(GoogleMapContext);
  const dispatch = useDispatch();
  const toast = useToast();
  const searchParams = useSearchParams();
  const nextRouter = useRouter();

  const {
    isOpen: isSearchModalOpen,
    onOpen: onSearchModalOpen,
    onClose: onSearchModalClose,
  } = useDisclosure();

  const user = useSelector((state) => state.user.userDetails);
  const practitionerFilters = useSelector((state) => state.practitionerFilters);
  const utilityData = props.search;
  const [isLoading, setIsLoading] = useState(false);
  const [searchOrder, setSearchOrder] = useState(null);
  const [lastSearch, setLastSearch] = useState(null);
  const [searchResults, setSearchResults] = useState(props.serverResults);
  const [totalSearchCount, setTotalSearchCount] = useState(0);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [searchPage, setSearchPage] = useState(0);
  const [urlValues, setUrlValues] = useState(null);
  const [urlSearch, setUrlSearch] = useState(
    () => props.search?.original ?? [],
  );
  const [urlHasLocation, setUrlHasLocation] = useState(initialHasLocation);
  const initialLocationText = props.search?.location?.place
    ? toTitleCase(props.search.location.place)
    : props.search?.location?.outcode
      ? props.search.location.outcode.toUpperCase()
      : null;
  const [locationText, setLocationText] = useState(initialLocationText);
  const [locationTitleText, setLocationTitleText] =
    useState(initialLocationText);
  const [modalityText, setModalityText] = useState(
    props.search?.modality ? toTitleCase(props.search.modality) : null,
  );
  const [doneInitial, setDoneInitial] = useState(false);
  const [showSearchCount, setShowSearchCount] = useState(false);
  const [urlPracticeArea, setUrlPracticeArea] = useState(
    props.search?.practiceArea?.slug ?? null,
  );
  const [urlModality, setUrlModality] = useState(null);

  const initialLocationSlug = useMemo(() => {
    const place = props.search?.location?.place;
    const outcode = props.search?.location?.outcode;
    return toSlug(place || outcode || "");
  }, [props.search, toSlug]);

  const currentLocationSlug = useMemo(() => {
    if (!urlHasLocation) return initialLocationSlug;
    const first = urlSearch?.[0];
    return toSlug(first || initialLocationSlug);
  }, [urlHasLocation, urlSearch, initialLocationSlug, toSlug]);
  const pathSlugForMeta = useMemo(() => {
    const q = nextRouter.query?.search;
    const fromQuery = q == null ? null : Array.isArray(q) ? q[0] : q;
    // asPath + canonicalPath first: router.query is often {} on SSR / some crawls,
    // which previously forced the generic "Local Counselling Services" title in <Head>.
    const raw =
      segmentAtCounselling(nextRouter.asPath, 0) ||
      segmentAtCounselling(props.canonicalPath, 0) ||
      fromQuery ||
      props.search?.original?.[0] ||
      urlSearch?.[0];
    return typeof raw === "string" && raw.trim() ? raw.trim() : null;
  }, [
    nextRouter.asPath,
    props.canonicalPath,
    nextRouter.query?.search,
    props.search?.original,
    urlSearch,
  ]);
  const locationForMeta = useMemo(() => {
    if (locationText) return locationText;
    const place = props.search?.location?.place;
    const outcode = props.search?.location?.outcode;
    if (place) return toTitleCase(place);
    if (outcode) return outcode.toUpperCase();
    if (!urlHasLocation) return null;
    if (!pathSlugForMeta) return null;
    return toTitleCase(pathSlugForMeta);
  }, [
    locationText,
    props.search?.location?.place,
    props.search?.location?.outcode,
    urlHasLocation,
    pathSlugForMeta,
    toTitleCase,
  ]);
  const topicForMeta = useMemo(() => {
    if (modalityText) return modalityText;
    if (props.search?.modality) return toTitleCase(props.search.modality);
    const topicSlug =
      (urlHasLocation ? urlSearch?.[1] : urlSearch?.[0]) ||
      segmentAtCounselling(nextRouter.asPath, 1) ||
      segmentAtCounselling(props.canonicalPath, 1) ||
      (urlHasLocation ? null : pathSlugForMeta);
    return topicSlug ? toTitleCase(topicSlug) : null;
  }, [
    modalityText,
    props.search?.modality,
    urlHasLocation,
    urlSearch,
    nextRouter.asPath,
    props.canonicalPath,
    pathSlugForMeta,
    toTitleCase,
  ]);
  const clientMeta = useMemo(
    () =>
      buildCounsellingMeta({
        location: locationForMeta,
        topic: topicForMeta,
      }),
    [locationForMeta, topicForMeta],
  );

  // Keep SSR / pre-hydration meta from getStaticProps; only update Head after the
  // router is ready so crawlers and first paint never see the generic placeholder.
  const effectiveMeta = useMemo(() => {
    const serverMeta = props.pageMeta;
    if (!nextRouter.isReady) {
      return serverMeta ?? clientMeta;
    }
    if (
      serverMeta &&
      isGenericCounsellingMeta(clientMeta) &&
      !isGenericCounsellingMeta(serverMeta)
    ) {
      return serverMeta;
    }
    return clientMeta;
  }, [nextRouter.isReady, props.pageMeta, clientMeta]);

  useEffect(() => {
    const debugMeta =
      process.env.NODE_ENV === "development" ||
      (typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("debugMeta") === "1");
    if (!debugMeta) return;
    // eslint-disable-next-line no-console -- intentional debug for meta / Head
    console.log("[PractitionerSearch meta]", {
      effectiveMeta,
      clientMeta,
      pageMeta: props.pageMeta,
      routerIsReady: nextRouter.isReady,
      pathSlugForMeta,
      locationForMeta,
      topicForMeta,
      urlHasLocation,
      urlSearch,
      routerQuerySearch: nextRouter.query?.search,
      propsOriginal: props.search?.original,
      asPath: nextRouter.asPath,
      canonicalPath: props.canonicalPath,
      slugFromAsPath: segmentAtCounselling(nextRouter.asPath, 0),
      slugFromCanonical: segmentAtCounselling(props.canonicalPath, 0),
    });
  }, [
    effectiveMeta,
    clientMeta,
    props.pageMeta,
    nextRouter.isReady,
    pathSlugForMeta,
    locationForMeta,
    topicForMeta,
    urlHasLocation,
    urlSearch,
    nextRouter.query?.search,
    nextRouter.asPath,
    props.search?.original,
    props.canonicalPath,
  ]);

  const getLocationId = (slug) => {
    const location = locationMapper[slug];
    return location;
  };
  const specialItems = specialLocationMap[getLocationId(currentLocationSlug)];

  let specialLocationComboItems = null;
  if (urlSearch.length > 1) {
    const secondItem = modalityMapper[urlSearch[1]] ?? urlSearch[1];
    specialLocationComboItems =
      specialLocationComboMap[`${urlSearch[0]}-${secondItem}`];
  }

  /**
   *
   * @param {Object.<string, string | null>} updateFields - Fields to update in the URL. If a field is
   * set to null then it is removed from URL. If a value isn't updated, do not
   * provide it.
   */
  const updateUrlSearch = useCallback(
    /**
     *
     * @param {Object.<string, string | null>} updateFields - Fields to update in the URL. If a field is
     * set to null then it is removed from URL. If a value isn't updated, do not
     * provide it.
     */
    (updateFields) => {
      // When we don't have any fields to update, early out
      if (Object.keys(updateFields).length < 1) {
        return;
      }

      if ("location" in updateFields) {
        setLocationText(toTitleCase(updateFields.location));
        setLocationTitleText(toTitleCase(updateFields.location));
      }
      if ("modality" in updateFields) {
        setModalityText(toTitleCase(updateFields.modality));
      }

      const _updateFields = Object.fromEntries(
        Object.entries(updateFields).map(([key, value]) => [
          key,
          typeof value === "string"
            ? value.toLowerCase().trim().replace(/\s+/g, "-")
            : null,
        ]),
      );
      const search = Router.query?.search ?? [];
      if (!urlHasLocation) {
        setUrlSearch(search);
        const updateUrl = `/counselling${search.length > 0 ? "/" : ""}${search.join("/")}${window.location.search}`;
        Router.push(updateUrl, updateUrl, { shallow: true });
        return;
      }

      const [location, modality] = search;
      let newSearch = [...search];
      const { location: newLocation, modality: newModality } = _updateFields;
      // If we don't have a location or newLocation then we can't build a URL
      if (!newLocation && !location) {
        newSearch = [];
      } else {
        // We need to use 'in' here as it matters if the key exists
        if ("location" in _updateFields) {
          newSearch[0] = newLocation;
        }
        if ("modality" in _updateFields && _updateFields.modality !== null) {
          newSearch[1] = newModality;
        } else if (modality) {
          newSearch[1] = modality;
        }
      }
      // Remove any nulls from the array
      newSearch = newSearch.filter(Boolean);
      setUrlSearch(newSearch);
      // setLocationText(newLocation ?? location);

      // When our old array and new array are equivalent, no need to update URL
      if (isEqual(search, newSearch)) {
        return;
      }

      const updateUrl = `/counselling${newSearch.length > 0 ? "/" : ""}${newSearch.join("/")}${window.location.search}`;
      Router.push(updateUrl, updateUrl, { shallow: true });
    },
    [urlHasLocation],
  );

  useEffect(() => {
    if (
      isServer ||
      !googleMapsLoaded ||
      doneInitial ||
      window.localStorage.getItem("wdUsePrev") === "true"
    ) {
      return;
    }

    const geocoder = new googleMaps.geocoding.Geocoder();
    const findLocation = async () => {
      const { location, modality } = props.search;
      const { outcode, place } = location;
      let updateFields = {
        resultIndex: 0,
      };
      /**
       * @type {google.maps.GeocoderRequest}
       */
      const geocodeParams = {};
      if (place) {
        geocodeParams.address = place;
      } else if (outcode) {
        geocodeParams.componentRestrictions = {
          postalCode: outcode,
        };
      }

      const practiceArea = searchParams.get("practiceArea");
      if (practiceArea) {
        updateFields = {
          ...updateFields,
          therapistPracticeAreas: [{ value: parseInt(practiceArea, 10) }],
        };
      }

      // No point in processing if there's nothing
      if (props.search.original.length === 0) {
        setUrlValues(updateFields);
        return;
      }
      // Is the location actually a modality?
      const isModality = utilityData.therapistPractitionerModalities.find(
        ({ label }) =>
          label.toLowerCase() === props.search.original[0].toLowerCase(),
      );

      if (
        Object.keys(geocodeParams).length > 0 &&
        ((place && place.toLowerCase() !== "online") || outcode) &&
        !isModality
      ) {
        try {
          // Don't need to check array length here as no results throws error
          const locationResult = await geocoder.geocode(geocodeParams);
          const [selected] = locationResult.results;
          // For a given location, gets its bounds then converts the distance between
          // them to miles, allowing us to calculate a radius for arbitrary locations
          const radius = Math.round(
            calculateLocationRadius(selected) * METERS_TO_MILES,
          );
          updateFields = {
            ...updateFields,
            location: selected.geometry.location.toJSON(),
            address: selected.formatted_address,
            distance: radius,
          };
          setSearchOrder("distance");
        } catch {
          Router.replace("/counselling", "/counselling", {
            shallow: true,
          });
          toast(missingLocation(place ?? outcode));
          updateUrlItem("location", null);
        }
      }

      const modalities = isModality
        ? props.search.original
        : props.search.original.slice(1);
      setUrlHasLocation(!isModality);

      // When modality is set and it's in our modalities array, search by it
      if (modalities.length > 0) {
        const items = [];
        for (const mod of modalities) {
          const modalityItem = utilityData.therapistPractitionerModalities.find(
            ({ label }) => label.toLowerCase() === mod.toLowerCase(),
          );

          if (modalityItem) {
            items.push(modalityItem);
          }
        }
        if (items.length > 0) {
          updateFields = {
            ...updateFields,
            therapistPractitionerModalities: items,
          };
        }
      }
      setDoneInitial(true);
      setUrlValues(updateFields);
    };
    findLocation();
  }, [
    googleMapsLoaded,
    googleMaps,
    props.search,
    utilityData,
    dispatch,
    toast,
    searchParams,
    doneInitial,
  ]);

  useEffect(() => {
    if (isServer) return;

    const wasBack = window.localStorage.getItem("wdUsePrev") === "true";
    if (wasBack) {
      try {
        const searchQuery = JSON.parse(
          window.localStorage.getItem("wdPrevSearch"),
        );
        setUrlValues({
          ...searchQuery.search,
          therapistPracticeAreas: searchQuery.search.practiceAreas,
          therapistPractitionerModalities:
            searchQuery.search.practitionerModalities,
          genders: searchQuery.search.gender?.map((item) => item.value) ?? null,
          sessionTypes:
            searchQuery.search.sessionTypes?.map((item) => item.value) ?? null,
          clientGroups:
            searchQuery.search.clientGroups?.map((item) => item.value) ?? null,
          languages:
            searchQuery.search.languages?.map((item) => item.value) ?? null,
          ageGroups:
            searchQuery.search.ageGroups?.map((item) => item.value) ?? null,
          healthInsurers:
            searchQuery.search.healthInsurers?.map((item) => item.value) ??
            null,
          religions:
            searchQuery.search.religions?.map((item) => item.value) ?? null,
          ethnicities:
            searchQuery.search.ethnicities?.map((item) => item.value) ?? null,
          seed: searchQuery.order.orderBy?.seed ?? null,
        });
        setDoneInitial(true);
      } catch (e) {
        setUrlValues({});
        console.error("No cached search");
        console.error(e);
      }
    }
    window.localStorage.removeItem("wdPrevSearch");
    window.localStorage.setItem("wdUsePrev", "false");
  }, []);

  useEffect(() => {
    const selectedModalities =
      practitionerFilters.therapistPractitionerModalities.filter(
        ({ selected }) => selected === true,
      );
    updateUrlSearch({
      modality:
        selectedModalities.length > 0 ? selectedModalities[0].label : null,
    });
  }, [practitionerFilters.therapistPractitionerModalities, updateUrlSearch]);

  const updateUrlItem = async (key, item) => {
    if (key === "all" && item === null) {
      setUrlSearch([]);
      setLocationText(null);
      setLocationTitleText(null);
      setModalityText(null);
      setUrlHasLocation(false);
      Router.push(
        "/counselling" + window.location.search,
        "/counselling" + window.location.search,
        { shallow: true },
      );
      return;
    }
    let newUrlItems = [...urlSearch] ?? [];
    let updateHasLocation = urlHasLocation;

    if (key === "location") {
      let setItem = item ?? [];

      if (item) {
        setItem = setItem.split(",");
        const baseName = setItem[0].trim();
        const locationSlug = baseName.toLowerCase().replace(/\s+/g, "-");
        setSearchOrder("distance");
        if (setItem[0].trim() === setItem[1].trim()) {
          setLocationText(
            toTitleCase(`${setItem[0].trim()}, ${setItem[2].trim()}`),
          );
          setLocationTitleText(
            toTitleCase(`${setItem[0].trim()}, ${setItem[2].trim()}`),
          );
        } else {
          setLocationText(toTitleCase(item));
          setLocationTitleText(toTitleCase(item));
        }

        if (urlSearch.length > 0) {
          newUrlItems = urlHasLocation
            ? [locationSlug, ...urlSearch.slice(1)]
            : [locationSlug, ...urlSearch];
        } else {
          newUrlItems = [locationSlug];
        }
        updateHasLocation = true;
      } else {
        setLocationText(null);
        setLocationTitleText(null);
        if (urlHasLocation) {
          newUrlItems = newUrlItems.length > 1 ? [...newUrlItems.slice(1)] : [];
          updateHasLocation = false;
        }
      }
    } else {
      const priorModalities = lastSearch?.practitionerModalities ?? [];
      if (priorModalities.length > 0) {
        const alreadyHasModality = priorModalities.find(
          (mod) => mod.label === item,
        );
        let urlItems;

        if (alreadyHasModality) {
          urlItems = [
            ...priorModalities
              .filter((mod) => mod.label !== item)
              .map((mod) => mod.label),
          ];
          setModalityText(urlItems.length > 0 ? urlItems[0] : null);
        } else {
          urlItems = [...priorModalities.map((mod) => mod.label), item];
          setModalityText(item);
        }

        if (urlHasLocation) {
          newUrlItems = [newUrlItems[0], ...urlItems];
        } else {
          newUrlItems = urlItems;
        }
      } else {
        newUrlItems = urlHasLocation ? [newUrlItems[0], item] : [item];
      }
    }

    // Slugify modalities
    for (let i = 0; i < newUrlItems.length; i++) {
      const item = newUrlItems[i];
      const modSlug = item.toLowerCase().replace(/\s+/g, "-");
      newUrlItems[i] = modSlug;
    }

    newUrlItems = Array.from(new Set(newUrlItems));

    const updateUrl = `/counselling/${newUrlItems.join("/")}${window.location.search}`;
    await Router.push(updateUrl, updateUrl, { shallow: true });
    setUrlSearch(newUrlItems);
    setUrlHasLocation(updateHasLocation);
  };

  const doSearch = useCallback(
    async (searchQuery, page = 0, orderBy = null) => {
      if (isLoading || isServer) return;

      if (page === 0) {
        setSearchPage(0);
      }
      setIsLoading(true);
      let search = searchQuery;

      if (!searchQuery) {
        search = lastSearch ?? {};
      } else {
        setLastSearch(searchQuery);
      }

      try {
        let ignoreUserId = null;
        if (user) {
          ignoreUserId = user.id;
        }

        const newQuery = {
          practitionerType: "therapist",
          resultIndex: page * 20,
          resultItems: 20,
          ignoreUserId,
          orderBy: {
            ascending: true,
            value: search.location?.lat ? "distance" : null,
            seed: searchQuery?.seed ?? searchSeed,
          },
        };
        const res = await postData(
          null,
          {
            ...search,
            ...newQuery,
          },
          `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}search${isbot(navigator.userAgent) ? "?skip=true" : ""}`,
          process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
        );

        window.localStorage.setItem(
          "wdPrevSearch",
          JSON.stringify({ search, order: newQuery }),
        );
        window.localStorage.setItem("wdUsePrev", "false");

        if (page > 0) {
          setSearchResults(searchResults.concat(res.results));
        } else {
          setSearchResults(res.results);
        }
        setTotalSearchCount(res.recordCount);
        setShowSearchCount(Object.keys(search).length > 1);
      } catch (e) {
        console.error(e);
        console.error("Something went wrong when fetching results");
      }

      setIsLoading(false);
    },
    [user, isLoading, lastSearch, searchOrder, searchResults],
  );

  const joinWithAnd = (arr) => {
    if (arr.length === 0) {
      return "";
    } else if (arr.length === 1) {
      return arr[0];
    } else if (arr.length === 2) {
      return arr.join(" and ");
    } else {
      const lastElement = arr[arr.length - 1];
      return arr.slice(0, -1).join(", ") + " and " + lastElement;
    }
  };

  return (
    <div className="flex flex-col w-full h-full min-h-[calc(100vh_-_180px)]">
      {nextRouter.isReady && (
        <Head>
          <title>{effectiveMeta.title}</title>
          <meta name="description" content={effectiveMeta.description} />
          <meta name="og:description" content={effectiveMeta.description} />
        </Head>
      )}
      {isLoading && (
        <LoadingSpinner message="Retrieving more practitioners..." />
      )}

      {selectedPractitioner !== null && (
        <BookingRequestModal
          show={selectedPractitioner}
          close={() => setSelectedPractitioner(null)}
          practitionerId={selectedPractitioner.userId}
          profileId={selectedPractitioner.profileId}
          practitionerName={selectedPractitioner.fullname}
          practitionerType="therapist"
        />
      )}

      {showLoginModal && (
        <LogInModal
          onCloseModal={() => setShowLoginModal(false)}
          title="Login"
          noRedirect
        />
      )}

      <div className="w-full bg-brand text-white flex items-center justify-center">
        <div className="max-w-[1300px] w-full p-4">
          {urlHasLocation ? (
            <>
              {locationText?.toLowerCase() !== "online" ? (
                urlSearch[1] ? (
                  <h1 className="font-bold text-xl sm:text-3xl">
                    Counsellors and therapists near &apos;{locationText}&apos;,
                    offering{" "}
                    {modalityText
                      ? modalityText
                      : joinWithAnd(urlSearch.slice(1).map(toTitleCase))}
                  </h1>
                ) : (
                  <h1 className="font-bold text-xl sm:text-3xl">
                    Counsellors and therapists near &apos;{locationText}&apos;
                  </h1>
                )
              ) : urlSearch[1] ? (
                <h1 className="font-bold text-xl sm:text-3xl">
                  Counsellors and therapists that offer services for{" "}
                  {joinWithAnd(urlSearch.map(toTitleCase))}
                </h1>
              ) : (
                <h1 className="font-bold text-xl sm:text-3xl">
                  Counsellors and therapists that offer online services
                </h1>
              )}
              <h2 className="text-sm sm:text-lg font-light">
                Welldoing is the UK’s leading therapist-matching service.
              </h2>
            </>
          ) : (
            <>
              {urlSearch.length > 0 ? (
                <h1 className="font-bold text-xl sm:text-3xl">
                  Here are our Welldoing counsellors and therapists that offer
                  services for {joinWithAnd(urlSearch.map(toTitleCase))}
                </h1>
              ) : (
                <h1 className="font-bold text-xl sm:text-3xl">
                  Here are our Welldoing counsellors and therapists
                </h1>
              )}
              <h4 className="text-sm sm:text-lg font-light">
                All members are verified, qualified and professionally trained
                psychotherapists, counsellors and psychologists
              </h4>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 flex gap-10 w-full max-w-[1300px] self-center p-4">
        <div className="hidden md:block shadow-lg rounded-lg flex-1 overflow-hidden w-full max-w-[420px] h-min sticky top-[80px]">
          <SearchForm
            onSubmit={doSearch}
            initialValues={urlValues}
            defaultData={props.search}
            onUrlItemUpdate={updateUrlItem}
            searchOrder={searchOrder}
            initialLocation={
              locationText?.toLowerCase() === "online" ? null : locationText
            }
            locationText={
              locationText?.toLowerCase() === "online" ? null : locationText
            }
          />
        </div>
        <div className="flex-1 flex flex-col gap-4 relative">
          <div className="flex flex-col sticky top-[80px] bg-white z-[100] py-2 px-5 -mx-5 border-0">
            <div className="flex flex-row justify-between items-center">
              {showSearchCount ? (
                <span className="hidden md:block text-sm">
                  Showing <b>{searchResults.length}</b> of{" "}
                  <b>{totalSearchCount}</b> therapists found
                </span>
              ) : (
                <span className="hidden md:block text-sm">
                  Showing all therapists
                </span>
              )}
              <button
                className="block md:hidden rounded-full bg-brand text-white font-bold px-4 py-2"
                onClick={onSearchModalOpen}
              >
                Filter search here
              </button>
            </div>
            <div className="mt-2 rounded-lg bg-brand/10 border border-brand/20 text-brand p-4">
              All therapists and counsellors comply with{" "}
              <Link href="/welldoing-charter" prefetch={false} target="_blank">
                <span className="underline cursor-pointer">our charter</span>
              </Link>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl bg-brand-alt px-6 py-6">
            <div>
              <h2 className="text-[28px] md:text-[32px] font-extrabold text-white leading-tight">
                Try MatchPlus
              </h2>
              <p className="text-white text-base md:text-lg leading-snug">
                Personalised matching help from our experts
              </p>
            </div>
            <Button
              className="bg-brand border border-brand font-bold text-white hover:text-brand/90 hover:bg-white/90 transition rounded-full px-4 py-2 pointer-events-auto"
              onClick={() => Router.push("/personalised-matching-service")}
            >
              Get started
            </Button>
          </div>

          {searchResults.map((item) => (
            <PractitionerCard
              onShowLogin={() => setShowLoginModal(true)}
              practitioner={item}
              key={item.userId}
              onOpenRequest={setSelectedPractitioner}
              selectedPracticeAreas={lastSearch?.practiceAreas ?? null}
            />
          ))}

          {searchResults.length === 0 && (
            <div className="self-center my-5 text-center flex flex-col items-center gap-2 px-5">
              <p>
                No therapists meet your search criteria – consider broadening
                your search by removing some of your filters. If you need
                personalised assistance, use our{" "}
                <Link href="/personalised-matching-service" prefetch={false}>
                  <span className="underline cursor-pointer text-brand font-bold">
                    MatchPlus
                  </span>
                </Link>{" "}
                service.
              </p>
            </div>
          )}

          {specialItems && (
            <div className="rounded-3xl shadow-lg overflow-hidden flex flex-col p-[15px] gap-2">
              <h2 className="text-xl font-bold text-brand-text mt-[15px]">
                {`Mental health support and therapy in ${locationText ? toTitleCase(locationText) : "your area"}`}
              </h2>
              <ul className="list-disc">
                {specialItems.map((item) => (
                  <li key={item.title} className="mx-[15px] pt-[15px]">
                    <div className="flex flex-col gap-2 sm:gap-5 md:gap-2 lg:gap-2">
                      <h3 className="text-lg font-bold text-brand-text">
                        {item.title}
                      </h3>
                      <p className="w-full whitespace-break-spaces text-lg text-gray-600">
                        {item.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {specialLocationComboItems && (
            <div className="rounded-3xl shadow-lg overflow-hidden flex flex-col p-[15px] gap-2">
              <p className="text-xl font-bold text-brand-text whitespace-break-spaces mt-[15px]">
                {specialLocationComboItems.header}
              </p>
              <div
                className="w-full whitespace-break-spaces text-lg text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: specialLocationComboItems.body,
                }}
              />
            </div>
          )}

          {searchResults.length < totalSearchCount && (
            <div className="flex items-center justify-center mb-4">
              <Button
                onClick={() => {
                  doSearch(null, searchPage + 1);
                  setSearchPage(searchPage + 1);
                }}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
      <Transition
        show={isSearchModalOpen}
        enter="duration-200 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="duration-300 ease-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        unmount={false}
      >
        <Dialog
          onClose={onSearchModalClose}
          className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-white z-[1000]"
          unmount={false}
        >
          <DialogPanel>
            <div className="flex flex-col h-screen p-0">
              <div className="flex items-center justify-end p-2">
                <Button
                  className="h-10 flex items-center justify-center"
                  onClick={onSearchModalClose}
                >
                  View results
                </Button>
              </div>
              <SearchForm
                onSubmit={doSearch}
                initialValues={urlValues}
                defaultData={props.search}
                onUrlItemUpdate={updateUrlItem}
                searchOrder={searchOrder}
                initialLocation={
                  locationText?.toLowerCase() === "online" ? null : locationText
                }
                locationText={
                  locationText?.toLowerCase() === "online" ? null : locationText
                }
              />

              <div className="mx-2 my-2">
                <Button
                  width="100%"
                  onClick={() => {
                    onSearchModalClose();
                  }}
                >
                  Update search
                </Button>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </Transition>
    </div>
  );
};

export default PractitionerSearch;
